import React, { useEffect, useState, useLayoutEffect, useRef, useMemo, useCallback, useContext, createContext } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  Download,
  Save,
  X,
  ShoppingCart,
  User,
  Calendar,
  CheckCircle,
  Clock,
  Truck,
  CreditCard,
  Banknote,
  Info,
  AlertCircle,
  XCircle,
  Receipt,
  DollarSign,
  FileText,
  ChevronDown,
  FileDown,
  Minus,
  Store,
  ExternalLink,
  Package,
  Contact,
  Globe,
} from 'lucide-react';
import { authAPI, salesOrdersAPI, clientsAPI, productsAPI, doctorsAPI, paymentsAPI, creditsAPI, whatsappAPI } from '../services/api';
import {
  ModernCard,
  ModernButton,
  ModernInput,
  ModernSelect,
  ModernOpticalSelect,
  ModernTable,
  ModernModal,
  ModernSearchBar,
  ModernLoadingSpinner
} from '../components/ui/ModernComponents';
import toast from 'react-hot-toast';
import Select from 'react-select';
import { formatCurrency } from '../utils/currency';
import { formatDateOnlyFr, toDateInputValue } from '../utils/dateFormat';
import {
  applyOrderOpticalToLensItems,
  getOpticalEyesFromItems,
  hasOpticalValue,
  isOpticalItemType,
  lensFormFromItem,
  numericFormValue,
  formatOpticalCorrectionSummary,
  parseOpticalNumber,
  resolveOpticalEditState,
  isOpticalModalExpanded,
} from '../utils/opticalValues';
import { resolveItemPrice, priceInputValue, hasExplicitPrice } from '../utils/itemPrice';
import EntitySelectWithAdd from '../components/ui/EntitySelectWithAdd';
import jsPDF from 'jspdf';
import { useAuth } from '../contexts/AuthContext';
import { generateOrderInvoicePDF } from '../utils/invoicePDF';
import { generateTicketPDF } from '../utils/ticketPDF';
import { generateFichePDF } from '../utils/fichePDF';
import {
  SALES_ORDER_STATUS_OPTIONS,
  normalizeLegacyStatus,
  getSalesOrderStatusLabel,
  getSalesOrderStatusListSelectClass,
  getSalesOrderStatusSelectChevronHex,
  LIVRE_STATUS_SET,
  WA_LIVREE_STATUS_SET,
  WA_EN_COURS_STATUS_SET
} from '../constants/salesOrderStatus';
import { COUPURE_OPTIONS } from '../constants/salesOrderCoupure';
import {
  COMMANDE_ORIGINE_OPTIONS,
  COMMANDE_ORIGINE_MAGASIN,
  isCommandeEnLigne,
  getOrderVilleLabel,
} from '../constants/salesOrderOrigine';

/** Badge ville for commande en ligne */
function OrderVilleTag({ order, className = '' }) {
  if (!isCommandeEnLigne(order)) return null;
  const ville = getOrderVilleLabel(order);
  if (!ville) return null;
  const isCasa = ville === 'Casa';
  return (
    <span
      className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ${
        isCasa
          ? 'bg-sky-50 text-sky-800 ring-sky-200'
          : 'bg-violet-50 text-violet-800 ring-violet-200'
      } ${className}`}
      title={`Ville: ${ville}`}
    >
      {ville}
    </span>
  );
}

/** Context that carries transient saving/selection states so table columns stay stable. */
const OrderListCtx = createContext({
  statusSavingId: null,
  verreSavingId: null,
  openingOrderId: null,
  sendingWaOrderId: null,
  pdfDropdownOpen: null,
  setPdfDropdownOpen: () => {},
  setPdfMenuPos: () => {},
  pdfAnchorRef: { current: null },
  handlePatchStatus: () => {},
  handlePatchVerreTracking: () => {},
  handleEdit: () => {},
  loadOrderPayments: () => {},
  handleSendOrderValidationWhatsApp: () => {},
  handleDelete: () => {},
  generateInvoicePDF: () => {},
  generateTicketPDFForOrder: () => {},
  generateFichePDFForOrder: () => {},
  getOrderTotal: () => 0,
  getTotalPaid: () => 0,
  getClientName: () => '',
});

const PAGE_SIZE = 30;

const defaultForm = {
  client_id: '',
  doctor_id: '',
  doctor_name: '',
  doctor_phone: '',
  insurance: '',
  autre_mutuelle: '',
  order_date: '',
  date_reception: '',
  status: 'pas_encore',
  commande_origine: COMMANDE_ORIGINE_MAGASIN,
  order_note: '',
  left_product_id: '',
  right_product_id: '',
  frame_id: '',
  items: [], // Array of { product_id, item_type, side, quantity, price }
};

const paymentModes = [
  { value: 'espece', label: 'Espèce' },
  { value: 'cheque', label: 'Chèque' },
  { value: 'carte_bancaire', label: 'Carte bancaire' },
  { value: 'virement', label: 'Virement' },
];

const defaultPayment = { amount: '', mode: 'espece', note: '', cheque_number: '', payment_date: '' };

const getTodayDateInput = () => new Date().toISOString().slice(0, 10);

const freshPaymentForm = () => ({
  amount: '',
  mode: 'espece',
  note: '',
  cheque_number: '',
  payment_date: getTodayDateInput(),
});
const DEFAULT_ORDER_WA_TEMPLATE = `Bonjour {client_name},

Votre commande #{order_id} est validee.
Statut: {status}
Date: {order_date}
Total: {total}

Details:
{items}

Merci pour votre confiance.
Epoptique`;

const PDF_MENU_WIDTH = 192;
/** Approx. height of the 3-item PDF menu; used to flip above the button when the last row is near the bottom of the viewport */
const PDF_MENU_EST_HEIGHT = 132;

function computePdfMenuPosition(rect) {
  const margin = 8;
  let left = rect.right - PDF_MENU_WIDTH;
  left = Math.max(margin, Math.min(left, window.innerWidth - PDF_MENU_WIDTH - margin));
  const gap = 4;
  const h = PDF_MENU_EST_HEIGHT;
  let top = rect.bottom + gap;
  if (top + h > window.innerHeight - margin) {
    const aboveTop = rect.top - gap - h;
    if (aboveTop >= margin) {
      top = aboveTop;
    } else {
      top = Math.max(margin, window.innerHeight - margin - h);
    }
  }
  return { top, left };
}

function orderHasVerre(order) {
  return Array.isArray(order?.items) && order.items.some((it) => it.item_type === 'verre');
}

function orderHasLentilles(order) {
  return Array.isArray(order?.items) && order.items.some((it) => it.item_type === 'lentilles');
}

/** Monture ou verre sur la commande (hors lentilles / accessoires). */
function orderHasMontureOuVerre(order) {
  return (
    Array.isArray(order?.items) &&
    order.items.some((it) => it.item_type === 'monture' || it.item_type === 'verre')
  );
}

/**
 * Onglet « Commandes lentilles » : au moins une ligne lentilles, et aucune monture ni verre
 * (lentilles seules ou lentilles + accessoires uniquement).
 */
function orderIsLentillesOnlySection(order) {
  return orderHasLentilles(order) && !orderHasMontureOuVerre(order);
}

const MINI_SELECT_BASE =
  'h-10 min-w-0 flex-1 cursor-pointer appearance-none rounded-xl border bg-no-repeat px-3 pr-9 text-left text-[12px] font-semibold leading-none shadow-sm transition focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:opacity-45';

const MOBILE_SELECT_BASE =
  'h-11 w-full min-w-0 cursor-pointer appearance-none rounded-2xl border bg-no-repeat px-3.5 pr-10 text-left text-[13px] font-semibold leading-none shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:opacity-45';

function miniSelectStyle(chevronHex6) {
  const strokeEnc = `%23${chevronHex6}`;
  return {
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='${strokeEnc}' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
    backgroundPosition: 'right 0.85rem center',
    backgroundSize: '1rem',
  };
}

const MINI_SELECT_NEUTRAL = `${MINI_SELECT_BASE} border-slate-200 bg-white text-slate-700 hover:border-slate-300 focus:border-indigo-500 focus:ring-indigo-400/25`;
const MOBILE_SELECT_NEUTRAL = `${MOBILE_SELECT_BASE} border-slate-200/90 bg-white text-slate-700 hover:border-slate-300 focus:border-sky-500 focus:ring-sky-400/25`;
const MINI_SELECT_NEUTRAL_STYLE = miniSelectStyle('64748b');

/** Statut commande — select coloré selon la valeur affichée */
const OrderStatusListCell = React.memo(function OrderStatusListCell({ order, fullWidth = false, mobile = false }) {
  const { statusSavingId, handlePatchStatus: onPatch } = useContext(OrderListCtx);
  const busy = statusSavingId === order.id;
  const v = normalizeLegacyStatus(order.status);
  const selectSurface = getSalesOrderStatusListSelectClass(order.status);
  const chevronHex = getSalesOrderStatusSelectChevronHex(order.status);
  const selectStyle = useMemo(() => miniSelectStyle(chevronHex), [chevronHex]);
  const baseClass = mobile ? MOBILE_SELECT_BASE : MINI_SELECT_BASE;
  return (
    <div className={fullWidth || mobile ? 'w-full min-w-0' : 'w-full min-w-0 max-w-[10rem]'}>
      <select
        aria-label="Changer le statut de la commande"
        value={v}
        disabled={busy}
        onChange={(e) => onPatch(order.id, e.target.value)}
        className={`${baseClass} ${selectSurface} w-full`}
        style={selectStyle}
      >
        {SALES_ORDER_STATUS_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
});

/** Vérification verre + coupure — table: labels à gauche ; mobile: grille propre */
const OrderVerreCoupureCell = React.memo(function OrderVerreCoupureCell({ order, fullWidth = false, mobile = false }) {
  const { verreSavingId, handlePatchVerreTracking: onPatch } = useContext(OrderListCtx);
  const busy = verreSavingId === order.id;
  if (!orderHasVerre(order)) {
    return (
      <div className={mobile ? 'rounded-2xl border border-dashed border-slate-200 bg-slate-50/80 px-3 py-3 text-center text-xs text-slate-400' : ''}>
        <span className={mobile ? '' : 'inline-flex items-center rounded-lg border border-dashed border-slate-200 bg-slate-50/80 px-2 py-1 text-xs text-slate-400'}>
          Aucun verre
        </span>
      </div>
    );
  }
  const verreVal = order.verre_verifie == null || order.verre_verifie === '' ? '' : String(order.verre_verifie);
  const coupureVal = order.coupure == null || order.coupure === '' ? '' : String(order.coupure);

  if (mobile) {
    return (
      <div className="grid grid-cols-2 gap-2.5">
        <label className="flex min-w-0 flex-col gap-1.5 rounded-2xl bg-cyan-50/70 p-2.5 ring-1 ring-cyan-100">
          <span className="text-[10px] font-bold uppercase tracking-wider text-cyan-700">Verre</span>
          <select
            aria-label="Verifier verre"
            value={verreVal}
            disabled={busy}
            onChange={(e) => onPatch(order.id, { verre_verifie: e.target.value })}
            className={MOBILE_SELECT_NEUTRAL}
            style={MINI_SELECT_NEUTRAL_STYLE}
          >
            <option value="">—</option>
            <option value="oui">Oui</option>
            <option value="non">Non</option>
          </select>
        </label>
        <label className="flex min-w-0 flex-col gap-1.5 rounded-2xl bg-violet-50/70 p-2.5 ring-1 ring-violet-100">
          <span className="text-[10px] font-bold uppercase tracking-wider text-violet-700">Coupure</span>
          <select
            aria-label="Coupure"
            value={coupureVal}
            disabled={busy}
            onChange={(e) => onPatch(order.id, { coupure: e.target.value })}
            className={MOBILE_SELECT_NEUTRAL}
            style={MINI_SELECT_NEUTRAL_STYLE}
          >
            <option value="">—</option>
            {COUPURE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      </div>
    );
  }

  const selectWrap = fullWidth ? 'w-full min-w-0 flex-1' : 'w-full min-w-0 max-w-[10rem]';
  return (
    <div className="flex min-w-0 flex-col gap-1.5 py-0.5">
      <div className="flex min-w-0 items-center gap-1.5">
        <span className="w-[3.75rem] shrink-0 text-[10px] font-semibold uppercase tracking-wide text-cyan-700">Verre</span>
        <div className={selectWrap}>
          <select
            aria-label="Verifier verre"
            value={verreVal}
            disabled={busy}
            onChange={(e) => onPatch(order.id, { verre_verifie: e.target.value })}
            className={`${MINI_SELECT_BASE} ${MINI_SELECT_NEUTRAL} w-full`}
            style={MINI_SELECT_NEUTRAL_STYLE}
          >
            <option value="">-</option>
            <option value="oui">Oui</option>
            <option value="non">Non</option>
          </select>
        </div>
      </div>
      <div className="flex min-w-0 items-center gap-1.5">
        <span className="w-[3.75rem] shrink-0 text-[10px] font-semibold uppercase tracking-wide text-violet-700">Coupure</span>
        <div className={selectWrap}>
          <select
            aria-label="Coupure"
            value={coupureVal}
            disabled={busy}
            onChange={(e) => onPatch(order.id, { coupure: e.target.value })}
            className={`${MINI_SELECT_BASE} ${MINI_SELECT_NEUTRAL} w-full`}
            style={MINI_SELECT_NEUTRAL_STYLE}
          >
            <option value="">-</option>
            {COUPURE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
});

const WhatsAppIcon = ({ className = 'w-4 h-4' }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className={className}>
    <path d="M19.11 4.89A9.86 9.86 0 0 0 12.04 2c-5.45 0-9.88 4.43-9.88 9.88 0 1.74.46 3.44 1.33 4.94L2 22l5.35-1.4a9.82 9.82 0 0 0 4.69 1.19h.01c5.45 0 9.88-4.43 9.88-9.88a9.8 9.8 0 0 0-2.82-7.02Zm-7.07 15.23h-.01a8.13 8.13 0 0 1-4.14-1.13l-.3-.18-3.17.83.84-3.09-.2-.32a8.2 8.2 0 0 1-1.27-4.35c0-4.52 3.68-8.2 8.21-8.2a8.13 8.13 0 0 1 5.82 2.42 8.13 8.13 0 0 1 2.39 5.79c0 4.52-3.68 8.2-8.17 8.2Zm4.49-6.13c-.25-.12-1.47-.73-1.7-.82-.23-.08-.39-.12-.56.12-.17.24-.64.82-.79.99-.14.16-.29.18-.53.06-.25-.12-1.05-.39-1.99-1.24-.73-.65-1.22-1.46-1.36-1.7-.14-.24-.01-.37.11-.49.11-.11.25-.29.37-.43.12-.14.17-.25.25-.41.08-.16.04-.31-.02-.43-.06-.12-.56-1.35-.77-1.85-.2-.48-.4-.41-.56-.42h-.48c-.16 0-.43.06-.65.31-.22.24-.85.83-.85 2.02s.88 2.35 1 2.51c.12.16 1.74 2.66 4.21 3.73.59.25 1.05.4 1.41.51.59.19 1.13.17 1.56.1.47-.07 1.47-.6 1.68-1.18.21-.58.21-1.08.15-1.18-.06-.09-.23-.14-.48-.26Z" />
  </svg>
);

/** Memoised action buttons — reads transient state from context, re-renders only when needed */
const OrderActionsCell = React.memo(function OrderActionsCell({ order }) {
  const {
    openingOrderId,
    sendingWaOrderId,
    pdfDropdownOpen,
    setPdfDropdownOpen,
    setPdfMenuPos,
    pdfAnchorRef,
    handleEdit,
    loadOrderPayments,
    handleSendOrderValidationWhatsApp,
    handleDelete,
    generateInvoicePDF,
    generateTicketPDFForOrder,
    generateFichePDFForOrder,
  } = useContext(OrderListCtx);

  const circleBase =
    'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border text-slate-700 shadow-sm transition hover:shadow-md disabled:pointer-events-none disabled:opacity-45 lg:h-7 lg:w-7';
  return (
    <div className="flex w-full flex-wrap items-center justify-between gap-1.5 lg:w-auto lg:flex-nowrap lg:justify-start lg:gap-1">
      <button
        type="button"
        title={openingOrderId === order.id ? 'Ouverture...' : 'Modifier'}
        disabled={openingOrderId !== null}
        onClick={() => handleEdit(order)}
        className={`${circleBase} border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100`}
      >
        <Edit className="w-4 h-4" />
      </button>
      <button
        type="button"
        title="Paiements"
        onClick={() => loadOrderPayments(order)}
        className={`${circleBase} border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100`}
      >
        <ShoppingCart className="w-4 h-4" />
      </button>
      <button
        type="button"
        title={sendingWaOrderId === order.id ? 'Envoi...' : 'Validation WhatsApp'}
        disabled={sendingWaOrderId === order.id}
        onClick={() => handleSendOrderValidationWhatsApp(order)}
        className={`${circleBase} border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100`}
      >
        <WhatsAppIcon className="w-4 h-4" />
      </button>
      <div className="relative pdf-dropdown-container">
        <button
          type="button"
          title="PDF"
          onClick={(e) => {
            if (pdfDropdownOpen === order.id) { setPdfDropdownOpen(null); return; }
            pdfAnchorRef.current = e.currentTarget;
            const rect = e.currentTarget.getBoundingClientRect();
            setPdfMenuPos(computePdfMenuPosition(rect));
            setPdfDropdownOpen(order.id);
          }}
          className={`${circleBase} border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100`}
        >
          <FileDown className="w-4 h-4" />
        </button>
      </div>
      <button
        type="button"
        title="Supprimer"
        onClick={() => handleDelete(order.id)}
        className={`${circleBase} border-red-200 bg-red-50 text-red-700 hover:bg-red-100`}
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
});

/** Mobile card — clean stacked layout for small screens */
const OrderMobileCard = React.memo(function OrderMobileCard({ order }) {
  const { getClientName, getOrderTotal, getTotalPaid } = useContext(OrderListCtx);
  const total = getOrderTotal(order);
  const paid = getTotalPaid(order);
  const rest = Math.max(0, total - paid);
  const paidTone =
    paid === 0
      ? 'bg-red-50 text-red-700 ring-red-100'
      : paid < total
        ? 'bg-amber-50 text-amber-800 ring-amber-100'
        : 'bg-emerald-50 text-emerald-700 ring-emerald-100';
  const restTone = rest > 0 ? 'bg-red-50 text-red-700 ring-red-100' : 'bg-emerald-50 text-emerald-700 ring-emerald-100';
  const clientName = getClientName(order.client_id, order.client_name);
  const initials = String(clientName || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || '')
    .join('') || '?';

  return (
    <article className="commandes-mobile-card overflow-hidden rounded-2xl border border-neutral-200/90 bg-white shadow-[0_8px_24px_-12px_rgba(15,23,42,0.18)]">
      <div className="h-1 w-full bg-gradient-to-r from-sky-500 via-teal-500 to-emerald-400" />

      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-sky-50 text-sm font-bold text-sky-700 ring-1 ring-sky-100">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate text-[15px] font-semibold leading-tight text-neutral-900">{clientName}</p>
                <p className="mt-0.5 truncate text-xs text-neutral-500">{order.client_phone || 'Pas de téléphone'}</p>
              </div>
              <span className="shrink-0 rounded-full bg-neutral-900 px-2.5 py-1 text-[11px] font-bold tabular-nums text-white">
                #{order.id}
              </span>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <OrderVilleTag order={order} />
              <span className="inline-flex items-center gap-1 rounded-full bg-neutral-50 px-2 py-1 text-[11px] font-medium text-neutral-600 ring-1 ring-neutral-200">
                <Calendar className="h-3 w-3 text-neutral-400" />
                {formatDateOnlyFr(order.created_at)}
              </span>
              <span className="inline-flex max-w-full items-center gap-1 rounded-full bg-neutral-50 px-2 py-1 text-[11px] font-medium text-neutral-600 ring-1 ring-neutral-200">
                <Contact className="h-3 w-3 shrink-0 text-neutral-400" />
                <span className="truncate">{order.employee_name?.trim() || '—'}</span>
              </span>
            </div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2">
          <div className="rounded-xl bg-slate-50 px-2 py-2.5 text-center ring-1 ring-slate-100">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Total</p>
            <p className="mt-1 text-[11px] font-bold leading-snug text-slate-800">{formatCurrency(total)}</p>
          </div>
          <div className={`rounded-xl px-2 py-2.5 text-center ring-1 ${paidTone}`}>
            <p className="text-[10px] font-semibold uppercase tracking-wide opacity-80">Payé</p>
            <p className="mt-1 text-[11px] font-bold leading-snug">{formatCurrency(paid)}</p>
          </div>
          <div className={`rounded-xl px-2 py-2.5 text-center ring-1 ${restTone}`}>
            <p className="text-[10px] font-semibold uppercase tracking-wide opacity-80">Reste</p>
            <p className="mt-1 text-[11px] font-bold leading-snug">{formatCurrency(rest)}</p>
          </div>
        </div>

        <div className="mt-4 space-y-3">
          <div className="rounded-2xl bg-white p-3 ring-1 ring-slate-200/80 shadow-sm">
            <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">Statut</p>
            <OrderStatusListCell order={order} fullWidth mobile />
          </div>
          <div className="rounded-2xl bg-white p-3 ring-1 ring-slate-200/80 shadow-sm">
            <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">Verre & Coupure</p>
            <OrderVerreCoupureCell order={order} fullWidth mobile />
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between gap-1 rounded-2xl bg-slate-50 px-2 py-2 ring-1 ring-slate-100">
          <OrderActionsCell order={order} />
        </div>
      </div>
    </article>
  );
});

const VentesCommandes = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEditCreator = ['admin', 'manager'].includes(user?.role);
  const [creatorOptions, setCreatorOptions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [listFilters, setListFilters] = useState({
    status: 'all',
    verre: 'all',
    coupure: 'all',
    payment: 'all',
    doctorId: 'all',
    employeeId: 'all',
    lensType: 'all',
    paymentMode: 'all',
    dateFrom: '',
    dateTo: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [editingId, setEditingId] = useState(null);
  const [assignDoctor, setAssignDoctor] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [payment, setPayment] = useState(defaultPayment);
  const [payments, setPayments] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [newPayment, setNewPayment] = useState(freshPaymentForm);
  const [orderPayments, setOrderPayments] = useState([]);
  const [orderTotal, setOrderTotal] = useState(0);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [editPayment, setEditPayment] = useState(freshPaymentForm);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState('monture');
  const [modalEditIdx, setModalEditIdx] = useState(null);
  const [modalForm, setModalForm] = useState({ product_id: '', item_type: 'monture', side: 'none', quantity: 1, price: '' });
  const [leftLensForm, setLeftLensForm] = useState({ sph: '', cyl: '', axe: '', addition: '' });
  const [rightLensForm, setRightLensForm] = useState({ sph: '', cyl: '', axe: '', addition: '' });
  const [lensRemarque, setLensRemarque] = useState('');
  
  // PD and Height measurements state
  const [pdMeasurements, setPdMeasurements] = useState({
    pd_right_verre_droit: '',
    pd_left_verre_droit: '',
    pd_right_verre_gauche: '',
    pd_left_verre_gauche: '',
    hauteur1: '',
    hauteur2: '',
    hauteur3: '',
    hauteur4: ''
  });
  
  // State for showing additional hauteur inputs
  const [showAdditionalHauteurs, setShowAdditionalHauteurs] = useState(false);
  const [modalError, setModalError] = useState('');
  const [modalSearchTerm, setModalSearchTerm] = useState('');
  const [formError, setFormError] = useState("");
  const [lensSelectionModal, setLensSelectionModal] = useState(false);
  const [lensSelectionTarget, setLensSelectionTarget] = useState('verre');
  const [lensSelectionType, setLensSelectionType] = useState('');
  const [leftLensProduct, setLeftLensProduct] = useState({ product_id: '', price: '' });
  
  // Payment reminder modal state
  const [showPaymentReminderModal, setShowPaymentReminderModal] = useState(false);
  const [pendingOrderData, setPendingOrderData] = useState(null);
  const [rightLensProduct, setRightLensProduct] = useState({ product_id: '', price: '' });
  const [paymentsByOrderId, setPaymentsByOrderId] = useState({});
  /** Single order being opened for edit (avoid reusing submitLoading / full-table blocking). */
  const [openingOrderId, setOpeningOrderId] = useState(null);
  /** Full commande fetch in progress — modal opens immediately, form shows overlay until ready */
  const [editOrderLoading, setEditOrderLoading] = useState(false);
  /** When opened from deleted list, reuse same modal in read-only mode */
  const [deletedPreviewMode, setDeletedPreviewMode] = useState(false);
  const [verreSavingId, setVerreSavingId] = useState(null);
  const [statusSavingId, setStatusSavingId] = useState(null);
  /** Liste : commandes (dont lentilles+monture/verre) | lentilles seules (+ accessoires) */
  const [lentillesView, setLentillesView] = useState('commandes');
  const [currentPage, setCurrentPage] = useState(1);

  // PDF dropdown state (menu rendered via portal to avoid table overflow clipping)
  const [pdfDropdownOpen, setPdfDropdownOpen] = useState(null);
  const [pdfMenuPos, setPdfMenuPos] = useState(null);
  const pdfAnchorRef = useRef(null);
  const searchLoadInitialized = useRef(false);
  
  // PDF Invoice ID Modal state
  const [showInvoiceIdModal, setShowInvoiceIdModal] = useState(false);
  const [pendingInvoiceData, setPendingInvoiceData] = useState(null);
  const [customInvoiceId, setCustomInvoiceId] = useState('');
  const [useCustomId, setUseCustomId] = useState(false);
  const [pendingPdfType, setPendingPdfType] = useState('facture'); // 'facture', 'ticket', 'fiche'
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [orderValidationTemplate, setOrderValidationTemplate] = useState(DEFAULT_ORDER_WA_TEMPLATE);
  const [orderStatusTemplates, setOrderStatusTemplates] = useState({
    confirmation: '',
    en_cours: '',
    livree: '',
  });
  const [sendingWaOrderId, setSendingWaOrderId] = useState(null);

  // Define addItem and updateItem at the top of the component
  const addItem = (item) => setForm(f => ({ ...f, items: [...f.items, item] }));
  const updateItem = (idx, newItem) => setForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? newItem : it) }));

  // 1. Add helpers for product name, sale price, and total
  const getProductName = useCallback((id) => {
    const p = products.find(pr => String(pr.id) === String(id));
    return p ? p.name : id;
  }, [products]);
  const getProductSalePrice = useCallback((id) => {
    const p = products.find(pr => String(pr.id) === String(id));
    return p ? p.sale_price : 0;
  }, [products]);
  const itemPrice = useCallback(
    (price, productId) => resolveItemPrice(price, getProductSalePrice(productId)),
    [getProductSalePrice]
  );
  const getItemTotal = (item) => itemPrice(item.price, item.product_id) * (item.quantity || 1);

  // Get filtered products based on type and search term
  const getFilteredProducts = (type, searchTerm = '') => {
    let filteredProducts = [];
    
    if (type === 'monture') {
      filteredProducts = products.filter(p => 
        (p.type === 'frames' || (p.category_name && p.category_name.toLowerCase().includes('monture'))) &&
        (p.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
         (p.brand && p.brand.toLowerCase().includes(searchTerm.toLowerCase())) ||
         (p.model && p.model.toLowerCase().includes(searchTerm.toLowerCase())))
      );
    } else if (type === 'verre') {
      filteredProducts = products.filter(p => 
        (p.type === 'glasses' || (p.category_name && p.category_name.toLowerCase().includes('verre'))) &&
        (p.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
         (p.brand && p.brand.toLowerCase().includes(searchTerm.toLowerCase())) ||
         (p.model && p.model.toLowerCase().includes(searchTerm.toLowerCase())))
      );
    } else if (type === 'accessoire') {
      filteredProducts = products.filter(p => 
        (p.type === 'accessories' || (p.category_name && p.category_name.toLowerCase().includes('accessoire'))) &&
        (p.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
         (p.brand && p.brand.toLowerCase().includes(searchTerm.toLowerCase())) ||
         (p.model && p.model.toLowerCase().includes(searchTerm.toLowerCase())))
      );
    } else if (type === 'lentilles') {
      filteredProducts = products.filter(p => 
        (p.type === 'contact_lenses' || (p.category_name && p.category_name.toLowerCase().includes('lentilles'))) &&
        (p.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
         (p.brand && p.brand.toLowerCase().includes(searchTerm.toLowerCase())) ||
         (p.model && p.model.toLowerCase().includes(searchTerm.toLowerCase())))
      );
    }
    
    return filteredProducts.map(p => ({
      value: p.id.toString(),
      label: p.name,
      product: p
    }));
  };

  // 2. Add modal state for adding/editing items
  const openModal = (type, idx = null) => {
    setModalType(type);
    setModalEditIdx(idx);
    setModalError('');
    if (idx !== null) {
      const item = form.items[idx];
      setModalForm(item);
      if (isOpticalItemType(type)) {
        const editState = resolveOpticalEditState(form.items, type, item);
        setLensSelectionType(editState.lensSelectionType);
        setLeftLensForm(editState.leftLensForm);
        setRightLensForm(editState.rightLensForm);
        if (editState.leftLensProduct) setLeftLensProduct(editState.leftLensProduct);
        if (editState.rightLensProduct) setRightLensProduct(editState.rightLensProduct);
        setLensRemarque(item.remarque || '');
      }
    } else {
        setModalForm({ product_id: '', item_type: type, side: 'none', quantity: 1, price: '' });
        setLeftLensForm({ sph: '', cyl: '', axe: '', addition: '' });
        setRightLensForm({ sph: '', cyl: '', axe: '', addition: '' });
        setLensRemarque('');
      }
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setModalForm({ product_id: '', item_type: 'monture', side: 'none', quantity: 1, price: '' });
    setLeftLensForm({ sph: '', cyl: '', axe: '', addition: '' });
    setRightLensForm({ sph: '', cyl: '', axe: '', addition: '' });
    setLensRemarque('');
    // Don't reset PD measurements - they should persist across lens additions
    setModalEditIdx(null);
    setModalError('');
    setModalSearchTerm('');
    setLensSelectionType('');
    setLeftLensProduct({ product_id: '', price: '' });
    setRightLensProduct({ product_id: '', price: '' });
  };
  const handleModalChange = (field, value) => {
    setModalForm(f => ({ ...f, [field]: value }));
  };
  const removeItem = (idx) => {
    setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  };

  /** Backend list already includes items + payments per order — hydrate map for compatibility. */
  const applyOrdersPayload = useCallback((list) => {
    const rows = Array.isArray(list) ? list : [];
    setOrders(rows);
    const payMap = {};
    rows.forEach((o) => {
      payMap[o.id] = o.payments || [];
    });
    setPaymentsByOrderId(payMap);
  }, []);

  const fetchOrders = useCallback(async ({ silent = false, search } = {}) => {
    if (!silent) setLoading(true);
    const q = search !== undefined ? String(search).trim() : String(searchTerm).trim();
    try {
      const list = await salesOrdersAPI.getSalesOrdersList(q ? { search: q } : {});
      applyOrdersPayload(list);
    } catch (err) {
      toast.error('Erreur lors du chargement des commandes');
      if (!silent) setOrders([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [applyOrdersPayload, searchTerm]);

  // Fetch clients for dropdown
  const fetchClients = useCallback(async () => {
    try {
      const res = await clientsAPI.getClients();
      setClients(res.data);
    } catch (err) {
      // ignore
    }
  }, []);

  // Fetch doctors for dropdown
  const fetchDoctors = useCallback(async () => {
    try {
      const res = await doctorsAPI.getDoctors();
      setDoctors(res.data);
    } catch (err) {
      // ignore
    }
  }, []);

  // Fetch payments for selected order (in details view)
  const fetchPayments = async (orderId) => {
    try {
      const res = await fetch(`/api/payments/order/${orderId}`);
      setPayments(await res.json());
    } catch {
      setPayments([]);
    }
  };

  // Fetch payments for a given order (for modal) — show immediately from list payload, refresh in background
  const loadOrderPayments = useCallback(async (order) => {
    setSelectedOrder(order);
    setOrderTotal(order.total || 0);
    const cached = order.payments || paymentsByOrderId[order.id] || [];
    setOrderPayments(cached);
    setNewPayment(freshPaymentForm());
    setShowPaymentModal(true);
    setPaymentLoading(true);
    try {
      const res = await paymentsAPI.getPaymentsForRef('sales_order', order.id);
      const data = res.data || [];
      setOrderPayments(data);
      setPaymentsByOrderId((prev) => ({ ...prev, [order.id]: data }));
      setOrders((prev) =>
        prev.map((o) => (o.id === order.id ? { ...o, payments: data } : o))
      );
    } catch {
      if (!cached.length) {
        setOrderPayments([]);
        setPaymentsByOrderId((prev) => ({ ...prev, [order.id]: [] }));
      }
    } finally {
      setPaymentLoading(false);
    }
  }, [paymentsByOrderId]);

  // Calculate payment status
  const getPaymentStatus = (order, payments) => {
    const paid = payments.reduce((sum, p) => sum + Number(p.amount), 0);
    if (paid === 0) return { label: 'Non payé', color: 'danger' };
    if (paid < (order.total || 0)) return { label: 'Partiel', color: 'warning' };
    return { label: 'Payé', color: 'success' };
  };

  // Add new payment
  const handleAddPayment = async (e) => {
    e.preventDefault();
    if (!selectedOrder) return;
    
    // Validate cheque number when payment mode is cheque
    if (newPayment.mode === 'cheque' && (!newPayment.cheque_number || newPayment.cheque_number.trim() === '')) {
      toast.error('Veuillez saisir le numéro de chèque');
      return;
    }
    if (!newPayment.payment_date || newPayment.payment_date.trim() === '') {
      toast.error('Veuillez saisir la date du paiement');
      return;
    }
    
    setPaymentLoading(true);
    try {
      await paymentsAPI.createPayment({
        reference_type: 'sales_order',
        reference_id: selectedOrder.id,
        client_id: selectedOrder.client_id,
        amount: newPayment.amount,
        mode: newPayment.mode,
        note: newPayment.note,
        cheque_number: newPayment.cheque_number,
        payment_date: newPayment.payment_date,
      });
      setNewPayment(freshPaymentForm());
      const payRes = await paymentsAPI.getPaymentsForRef('sales_order', selectedOrder.id);
      const payments = payRes.data || [];
      setOrderPayments(payments);
      setPaymentsByOrderId((prev) => ({ ...prev, [selectedOrder.id]: payments }));
      updateOrderPaymentsInList(selectedOrder.id, payments);
    } catch {
      // handle error
    }
    setPaymentLoading(false);
  };

  // Delete payment
  const handleDeletePayment = async (id) => {
    if (!window.confirm('Supprimer ce paiement ?')) return;
    setPaymentLoading(true);
    try {
      await paymentsAPI.deletePayment(id);
      const payRes = await paymentsAPI.getPaymentsForRef('sales_order', selectedOrder.id);
      const payments = payRes.data || [];
      setOrderPayments(payments);
      setPaymentsByOrderId((prev) => ({ ...prev, [selectedOrder.id]: payments }));
      updateOrderPaymentsInList(selectedOrder.id, payments);
    } catch {}
    setPaymentLoading(false);
  };

  // Start editing payment
  const startEditPayment = (payment) => {
    setEditingPaymentId(payment.id);
    setEditPayment({ 
      amount: payment.amount, 
      mode: payment.mode, 
      note: payment.note || '', 
      cheque_number: payment.cheque_number || '',
      payment_date: toDateInputValue(payment.payment_date) || getTodayDateInput(),
    });
  };

  // Cancel editing
  const cancelEditPayment = () => {
    setEditingPaymentId(null);
    setEditPayment(freshPaymentForm());
  };

  // Save edited payment
  const handleSaveEditPayment = async (e) => {
    e.preventDefault();
    if (!editingPaymentId) return;
    
    // Validate cheque number when payment mode is cheque
    if (editPayment.mode === 'cheque' && (!editPayment.cheque_number || editPayment.cheque_number.trim() === '')) {
      toast.error('Veuillez saisir le numéro de chèque');
      return;
    }
    if (!editPayment.payment_date || editPayment.payment_date.trim() === '') {
      toast.error('Veuillez saisir la date du paiement');
      return;
    }
    
    setPaymentLoading(true);
    try {
      await paymentsAPI.updatePayment(editingPaymentId, {
        amount: editPayment.amount,
        mode: editPayment.mode,
        note: editPayment.note,
        cheque_number: editPayment.cheque_number,
        payment_date: editPayment.payment_date,
      });
      setEditingPaymentId(null);
      setEditPayment(freshPaymentForm());
      const payRes = await paymentsAPI.getPaymentsForRef('sales_order', selectedOrder.id);
      const payments = payRes.data || [];
      setOrderPayments(payments);
      setPaymentsByOrderId((prev) => ({ ...prev, [selectedOrder.id]: payments }));
      updateOrderPaymentsInList(selectedOrder.id, payments);
    } catch {}
    setPaymentLoading(false);
  };

  const handleExportPayments = async () => {
    try {
      const response = await paymentsAPI.exportPayments({ reference_type: 'sales_order' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'payments_sales_orders.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      toast.error('Erreur lors de l\'export des paiements');
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [ordersList, clientsRes, doctorsRes, productsRes, waCfgRes] = await Promise.all([
          salesOrdersAPI.getSalesOrdersList(),
          clientsAPI.getClients().catch(() => ({ data: [] })),
          doctorsAPI.getDoctors().catch(() => ({ data: [] })),
          productsAPI.getProducts().catch(() => ({ data: [] })),
          whatsappAPI.getConfig().catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        applyOrdersPayload(ordersList);
        setClients(clientsRes.data || []);
        setDoctors(doctorsRes.data || []);
        setProducts(productsRes.data || []);
        const cfg = waCfgRes.data || {};
        if (cfg.order_validation_template) setOrderValidationTemplate(cfg.order_validation_template);
        setOrderStatusTemplates({
          confirmation: cfg.message_template_confirmation || '',
          en_cours: cfg.message_template_en_cours || '',
          livree: cfg.message_template_livree || '',
        });
      } catch (err) {
        toast.error('Erreur lors du chargement des commandes');
        setOrders([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Server-side search across all commandes (not limited to the 500 most recent)
  useEffect(() => {
    if (!searchLoadInitialized.current) {
      searchLoadInitialized.current = true;
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      if (cancelled) return;
      await fetchOrders({ silent: true, search: searchTerm });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchTerm, fetchOrders]);

  // Handle form input
  const handleChange = (e) => {
    if (!e || !e.target) return;
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  // 4. On submit, send form.items to backend
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (deletedPreviewMode) return;
    setFormError("");
    
    // Only validate client, status, and items
    if (!form.client_id) {
      setFormError("Veuillez sélectionner un client.");
      return;
    }
    if (!form.status) {
      setFormError("Veuillez sélectionner un statut.");
      return;
    }
    if (!form.items || form.items.length === 0) {
      setFormError("Veuillez ajouter au moins un article à la commande.");
      return;
    }
    
    // Check if payment is provided for new orders
    if (!editingId && (!payment.amount || payment.amount <= 0)) {
      // Show payment reminder modal for new orders
      const payload = prepareOrderPayload();
      setPendingOrderData(payload);
      setShowPaymentReminderModal(true);
      return;
    }
    
    // Proceed with order creation/update
    await createOrderWithPayment();
  };

  const prepareOrderPayload = () => {
    // Extract optical parameters from lens items
    let leftSph = null, leftCyl = null, leftAxis = null, leftAddition = null, leftProductId = null;
    let rightSph = null, rightCyl = null, rightAxis = null, rightAddition = null, rightProductId = null;
    
    // Find lens items and extract optical parameters
    const { left: leftLens, right: rightLens } = getOpticalEyesFromItems(form.items);
    
    if (leftLens) {
      leftSph = parseOpticalNumber(leftLens.sph);
      leftCyl = parseOpticalNumber(leftLens.cyl);
      leftAxis = parseOpticalNumber(leftLens.axe);
      leftAddition = parseOpticalNumber(leftLens.addition);
      leftProductId = leftLens.product_id || null;
    }

    if (rightLens) {
      rightSph = parseOpticalNumber(rightLens.sph);
      rightCyl = parseOpticalNumber(rightLens.cyl);
      rightAxis = parseOpticalNumber(rightLens.axe);
      rightAddition = parseOpticalNumber(rightLens.addition);
      rightProductId = rightLens.product_id || null;
    }
    
    const payload = {
      client_id: form.client_id,
      ...(editingId && canEditCreator && form.employee_id && form.employee_id !== form.original_employee_id ? { employee_id: form.employee_id } : {}),
      // Always include doctor_id if it exists in the form, regardless of assignDoctor state
      ...(form.doctor_id && form.doctor_id !== '' ? { doctor_id: form.doctor_id } : {}),
      insurance: form.insurance && form.insurance !== '' ? (form.insurance === 'Autre' && form.autre_mutuelle ? form.autre_mutuelle : form.insurance) : 'Aucune',
      ...(form.order_date && form.order_date !== '' ? { order_date: form.order_date } : {}),
      date_reception: form.date_reception && form.date_reception !== '' ? form.date_reception : null,
      status: form.status,
      items: form.items,
      store_id: user?.store_id || form.store_id || 1, // fallback to 1 if not available
      // Optical parameters - only send if lens exists for that eye
      ...(leftLens ? {
        left_sph: leftSph,
        left_cyl: leftCyl,
        left_axis: leftAxis,
        left_addition: leftAddition,
        left_product_id: leftProductId,
      } : {}),
      ...(rightLens ? {
        right_sph: rightSph,
        right_cyl: rightCyl,
        right_axis: rightAxis,
        right_addition: rightAddition,
        right_product_id: rightProductId,
      } : {}),
      // PD and Height measurements
      pd_right_verre_droit: pdMeasurements.pd_right_verre_droit && pdMeasurements.pd_right_verre_droit !== '' ? parseFloat(pdMeasurements.pd_right_verre_droit) : null,
      pd_left_verre_droit: pdMeasurements.pd_left_verre_droit && pdMeasurements.pd_left_verre_droit !== '' ? parseFloat(pdMeasurements.pd_left_verre_droit) : null,
      pd_right_verre_gauche: pdMeasurements.pd_right_verre_gauche && pdMeasurements.pd_right_verre_gauche !== '' ? parseFloat(pdMeasurements.pd_right_verre_gauche) : null,
      pd_left_verre_gauche: pdMeasurements.pd_left_verre_gauche && pdMeasurements.pd_left_verre_gauche !== '' ? parseFloat(pdMeasurements.pd_left_verre_gauche) : null,
      hauteur1: pdMeasurements.hauteur1 && pdMeasurements.hauteur1 !== '' ? parseFloat(pdMeasurements.hauteur1) : null,
      hauteur2: pdMeasurements.hauteur2 && pdMeasurements.hauteur2 !== '' ? parseFloat(pdMeasurements.hauteur2) : null,
      hauteur3: pdMeasurements.hauteur3 && pdMeasurements.hauteur3 !== '' ? parseFloat(pdMeasurements.hauteur3) : null,
      hauteur4: pdMeasurements.hauteur4 && pdMeasurements.hauteur4 !== '' ? parseFloat(pdMeasurements.hauteur4) : null,
      commande_origine:
        form.commande_origine === 'en_ligne' ? 'en_ligne' : 'magasin',
      order_note: form.order_note && form.order_note.trim() !== '' ? form.order_note.trim() : null,
    };
    
    return payload;
  };

  const createOrderWithPayment = async (paymentData = null) => {
    setSubmitLoading(true);
    const payload = paymentData || prepareOrderPayload();
    const paymentToUse = paymentData ? paymentData.payment : payment;
    
    try {
      if (editingId) {
        await salesOrdersAPI.updateSalesOrder(editingId, payload);
        toast.success('Commande modifiée');
      } else {
        const res = await salesOrdersAPI.createSalesOrder(payload);
        toast.success('Commande ajoutée');
        const createdOrderId = res.data.salesOrderId;
        
        // Create payment if amount is provided
        if (paymentToUse.amount && paymentToUse.amount > 0) {
          await paymentsAPI.createPayment({
            reference_type: 'sales_order',
            reference_id: createdOrderId,
            client_id: form.client_id,
            amount: paymentToUse.amount,
            mode: paymentToUse.mode,
            note: paymentToUse.note,
            cheque_number: paymentToUse.cheque_number,
            payment_date: paymentToUse.payment_date || getTodayDateInput(),
          });
          
          // The credit will be automatically created in the backend
          // when the payment is created, but we can also explicitly
          // create the credit if needed for immediate feedback
          const orderTotal = payload.items.reduce(
            (sum, item) => sum + itemPrice(item.price, item.product_id) * (item.quantity || 1),
            0
          );
          
          const remainingAmount = orderTotal - paymentToUse.amount;
          
          if (remainingAmount > 0) {
            // Credit will be automatically created by the backend
            // when the payment is processed
            // Explicitly create credit to ensure it's created
            try {
              await creditsAPI.createAutoCredit({
                client_id: form.client_id,
                reference_type: 'sales_order',
                reference_id: createdOrderId,
                total_amount: orderTotal,
                paid_amount: paymentToUse.amount,
                date: new Date().toISOString().split('T')[0]
              });
              
              toast.success(`Credit créé: ${formatCurrency(remainingAmount)}`);
            } catch {
              toast.error('Erreur lors de la création du crédit');
              // Don't fail the order creation if credit creation fails
            }
          }
        }
      }
      setShowForm(false);
      setForm(defaultForm);
      setEditingId(null);
      setFormError("");
      setShowPaymentReminderModal(false);
      setPendingOrderData(null);
      // Reset PD measurements after successful order creation/update
      setPdMeasurements({
        pd_right_verre_droit: '',
        pd_left_verre_droit: '',
        pd_right_verre_gauche: '',
        pd_left_verre_gauche: '',
        hauteur1: '',
        hauteur2: '',
        hauteur3: '',
        hauteur4: ''
      });
      fetchOrders({ silent: true });
    } catch (err) {
      // Check if it's a stock error
      if (err.response?.data?.error && err.response.data.error.includes('Insufficient stock')) {
        const stockError = err.response.data.error;
        const productIdMatch = stockError.match(/product ID (\d+)/);
        if (productIdMatch) {
          const productId = parseInt(productIdMatch[1]);
          const product = products.find(p => p.id === productId);
          const productName = product ? product.name : `Produit ${productId}`;
          toast.error(`Stock insuffisant pour ${productName}. Veuillez vérifier le stock disponible.`);
        } else {
          toast.error('Stock insuffisant pour un ou plusieurs produits. Veuillez vérifier le stock disponible.');
        }
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handlePaymentReminderResponse = async (createPayment) => {
    if (createPayment) {
      // User wants to create payment - show payment inputs in form
      setShowPaymentReminderModal(false);
      // The payment section should already be visible in the form
    } else {
      // User wants to create order without payment
      await createOrderWithPayment();
    }
  };

  // Update handleEdit to use the API service with authentication
  const handleEdit = useCallback(async (order, options = {}) => {
    const { includeDeleted = false, readOnly = false } = options;
    if (!order?.id) return;
    setDeletedPreviewMode(readOnly);
    setOpeningOrderId(order.id);
    setShowForm(true);
    setEditOrderLoading(true);
    setEditingId(order.id);
    try {
      if (doctors.length === 0) {
        await fetchDoctors();
      }

      const res = await salesOrdersAPI.getSalesOrder(order.id, includeDeleted ? { include_deleted: 1 } : {});
      const fullOrder = res.data;
      if (canEditCreator && !readOnly) {
        const employeesRes = await authAPI.getEmployees();
        const options = employeesRes.data.map(employee => ({ value: String(employee.id), label: employee.name }));
        if (fullOrder.employee_id && !options.some(option => option.value === String(fullOrder.employee_id))) {
          options.push({ value: String(fullOrder.employee_id), label: fullOrder.employee_name || 'Créateur actuel', isDisabled: true });
        }
        setCreatorOptions(options);
      }

      
      // Prescription lives on sales_orders — merge onto verre line items for editing
      let processedItems = applyOrderOpticalToLensItems([...(fullOrder.items || [])], fullOrder);
      
      // Determine if insurance is a custom "autre" mutuelle
      const isAutreMutuelle = fullOrder.insurance && 
        !['', 'Aucune', 'CNSS', 'CNOPS'].includes(fullOrder.insurance);
      
      const formData = {
        client_id: fullOrder.client_id,
        employee_id: fullOrder.employee_id ? String(fullOrder.employee_id) : '',
        original_employee_id: fullOrder.employee_id ? String(fullOrder.employee_id) : '',
        doctor_id: fullOrder.doctor_id ? fullOrder.doctor_id.toString() : '',
        doctor_name: fullOrder.doctor_name || '',
        doctor_phone: fullOrder.doctor_phone || '',
        insurance: isAutreMutuelle ? 'Autre' : (fullOrder.insurance || ''),
        autre_mutuelle: isAutreMutuelle ? fullOrder.insurance : '',
        order_date: toDateInputValue(fullOrder.created_at),
        date_reception: toDateInputValue(fullOrder.date_reception),
        status: normalizeLegacyStatus(fullOrder.status),
        items: processedItems,
        client_name: fullOrder.client_name,
        client_phone: fullOrder.client_phone,
        client_email: fullOrder.client_email,
        total: fullOrder.total,
        payments: fullOrder.payments || [],
        commande_origine:
          String(fullOrder.commande_origine || '').toLowerCase() === 'en_ligne'
            ? 'en_ligne'
            : COMMANDE_ORIGINE_MAGASIN,
        order_note: fullOrder.order_note || '',
      };
      
      setForm(formData);
      
      // Load PD measurements from the order
      setPdMeasurements({
        pd_right_verre_droit: numericFormValue(fullOrder.pd_right_verre_droit),
        pd_left_verre_droit: numericFormValue(fullOrder.pd_left_verre_droit),
        pd_right_verre_gauche: numericFormValue(fullOrder.pd_right_verre_gauche),
        pd_left_verre_gauche: numericFormValue(fullOrder.pd_left_verre_gauche),
        hauteur1: numericFormValue(fullOrder.hauteur1),
        hauteur2: numericFormValue(fullOrder.hauteur2),
        hauteur3: numericFormValue(fullOrder.hauteur3),
        hauteur4: numericFormValue(fullOrder.hauteur4),
      });

      if (hasOpticalValue(fullOrder.hauteur3) || hasOpticalValue(fullOrder.hauteur4)) {
        setShowAdditionalHauteurs(true);
      }
    } catch (err) {
      toast.error('Erreur lors du chargement de la commande');
      setShowForm(false);
      setEditingId(null);
      setDeletedPreviewMode(false);
    } finally {
      setEditOrderLoading(false);
      setOpeningOrderId(null);
    }
  }, [doctors.length, fetchDoctors, canEditCreator]);

  const handleEditById = useCallback(async (orderId, options = {}) => {
    if (!orderId) return;
    await handleEdit({ id: orderId }, options);
  }, [handleEdit]);

  // Handle delete
  const handleDelete = useCallback(async (id) => {
    if (!window.confirm('Supprimer cette commande ?')) return;
    try {
      await salesOrdersAPI.deleteSalesOrder(id);
      toast.success('Commande supprimée');
      fetchOrders({ silent: true });
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  }, [fetchOrders]);

  // Handle add new
  const handleAdd = () => {
    setDeletedPreviewMode(false);
    navigate('/ventes/nouveau');
  };

  useEffect(() => {
    const orderId = location?.state?.openDeletedOrderId;
    if (!orderId) return;
    handleEditById(orderId, { includeDeleted: true, readOnly: true });
    navigate(location.pathname, { replace: true, state: {} });
  }, [location, handleEditById, navigate]);

  // Helper to get client name — prefers embedded field from backend, falls back to local lookup
  const getClientName = useCallback((clientId, embeddedName) => {
    if (embeddedName) return embeddedName;
    const client = clients.find((c) => c.id === clientId);
    return client ? client.name : clientId;
  }, [clients]);

  // Inline item form state
  const [itemForm, setItemForm] = useState({ product_id: '', item_type: 'monture', side: 'none', quantity: 1, price: '' });
  const [editIdx, setEditIdx] = useState(null);
  const [itemError, setItemError] = useState('');

  const resetItemForm = () => setItemForm({ product_id: '', item_type: 'monture', side: 'none', quantity: 1, price: '' });

  const handleItemChange = (e) => {
    const { name, value } = e.target;
    setItemForm(f => ({ ...f, [name]: value }));
  };

  const handleAddOrUpdateItem = () => {
    if (!itemForm.product_id || !itemForm.quantity || !hasExplicitPrice(itemForm.price)) {
      setItemError('Tous les champs sont requis');
      return;
    }
    setItemError('');
    const price = itemPrice(itemForm.price, itemForm.product_id);
    const itemToSave = { ...itemForm, price };
    if (editIdx !== null) {
      updateItem(editIdx, itemToSave);
      setEditIdx(null);
    } else {
      // Allow multiple same products - no duplicate check
      addItem(itemToSave);
    }
    resetItemForm();
  };

  const startEditItem = (idx) => {
    setEditIdx(idx);
    setItemForm(form.items[idx]);
  };

  const cancelEditItem = () => {
    setEditIdx(null);
    resetItemForm();
  };

  // Helper to get total paid for an order
  const getTotalPaid = useCallback((order) => {
    const pArr = order.payments ?? paymentsByOrderId[order.id];
    return (Array.isArray(pArr) ? pArr : []).reduce((sum, p) => sum + Number(p.amount), 0);
  }, [paymentsByOrderId]);
  // Helper to get total amount (fallback to calculated if missing)
  const getOrderTotal = useCallback((order) => {
    if (typeof order.total === 'number') return order.total;
    if (order.items && Array.isArray(order.items)) {
      return order.items.reduce((sum, item) => sum + (Number(item.price) * Number(item.quantity)), 0);
    }
    return 0;
  }, []);

  const buildOrderItemsSummary = useCallback((order) => {
    if (!Array.isArray(order?.items) || order.items.length === 0) return '-';
    return order.items
      .map((it) => {
        const name = it.product_name || it.name || `Produit ${it.product_id || ''}`.trim();
        const qty = Number(it.quantity || 1);
        const price = Number(it.price || 0);
        const side = it.side && it.side !== 'none' ? ` (${it.side})` : '';
        return `- ${name}${side} x${qty} — ${formatCurrency(price * qty)}`;
      })
      .join('\n');
  }, []);

  const compileOrderValidationMessage = useCallback((order) => {
    const clientName = order.client_name || getClientName(order.client_id) || 'Client';
    const totalPaid = getTotalPaid(order);
    const details = buildOrderItemsSummary(order);
    const normalized = normalizeLegacyStatus(order.status);
    const baseTemplate =
      (WA_LIVREE_STATUS_SET.has(normalized)
        ? orderStatusTemplates.livree
        : WA_EN_COURS_STATUS_SET.has(normalized)
          ? orderStatusTemplates.en_cours
          : orderStatusTemplates.confirmation) || orderValidationTemplate || DEFAULT_ORDER_WA_TEMPLATE;
    const replacements = {
      '{client_name}': String(clientName),
      '{order_id}': String(order.id || ''),
      '{status}': getSalesOrderStatusLabel(order.status),
      '{order_date}': formatDateOnlyFr(order.created_at),
      '{total}': formatCurrency(getOrderTotal(order)),
      '{total_paid}': formatCurrency(totalPaid),
      '{items}': details,
      '{order_details}': details,
    };
    let out = baseTemplate;
    Object.entries(replacements).forEach(([k, v]) => {
      out = out.split(k).join(v);
    });
    return out.trim();
  }, [orderValidationTemplate, orderStatusTemplates, getClientName, getOrderTotal, getTotalPaid, buildOrderItemsSummary]);

  const handleSendOrderValidationWhatsApp = useCallback(async (order) => {
    if (!order?.client_id) {
      toast.error('Client introuvable pour cette commande');
      return;
    }
    const msg = compileOrderValidationMessage(order);
    if (!msg) {
      toast.error('Le message WhatsApp est vide');
      return;
    }
    setSendingWaOrderId(order.id);
    try {
      await whatsappAPI.sendOrderValidation({
        clientIds: [Number(order.client_id)],
        message: msg
      });
      toast.success(`Message de validation WhatsApp envoye (commande #${order.id})`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur envoi WhatsApp');
    } finally {
      setSendingWaOrderId(null);
    }
  }, [compileOrderValidationMessage]);

  // Helper to generate PDF invoice for a commande
  const generateInvoicePDF = useCallback(async (order) => {
    setPendingInvoiceData({
      order,
      clientName: order.client_name || getClientName(order.client_id),
      clientId: order.client_id
    });
    setPendingPdfType('facture');
    setCustomInvoiceId('');
    setUseCustomId(false);
    const orderPayments = order.payments || paymentsByOrderId[order.id] || [];
    if (orderPayments.length > 0) {
      const paymentLabels = { espece: 'Espèce', cheque: 'Chèque', carte_bancaire: 'Carte bancaire', virement: 'Virement bancaire', traite: 'Traite', effet: 'Effet de commerce' };
      setSelectedPaymentMethod(paymentLabels[orderPayments[0].mode] || 'Espèce');
    } else {
      setSelectedPaymentMethod('Espèce');
    }
    setShowInvoiceIdModal(true);
  }, [getClientName, paymentsByOrderId]);

  const generateTicketPDFForOrder = useCallback((order) => {
    const clientName = order.client_name || getClientName(order.client_id);
    const orderPayments = order.payments || paymentsByOrderId[order.id] || [];
    const enhancedItems = (order.items || []).map(item => ({
      ...item,
      product_name: item.product_name || getProductName(item.product_id)
    }));
    generateTicketPDF(order, clientName, enhancedItems, orderPayments);
  }, [getClientName, paymentsByOrderId, getProductName]);

  const generateFichePDFForOrder = useCallback((order) => {
    const clientName = order.client_name || getClientName(order.client_id);
    const orderPayments = order.payments || paymentsByOrderId[order.id] || [];
    const enhancedItems = (order.items || []).map(item => ({
      ...item,
      product_name: item.product_name || getProductName(item.product_id)
    }));
    generateFichePDF(order, clientName, enhancedItems, orderPayments);
  }, [getClientName, paymentsByOrderId, getProductName]);
  
  // Actually generate the PDF with the selected invoice ID
  const confirmGeneratePDF = async () => {
    if (!pendingInvoiceData) return;
    
    const { order, clientName } = pendingInvoiceData;
    const orderPayments = order.payments || paymentsByOrderId[order.id] || [];
    
    // Use items with product names from backend
    const enhancedItems = (order.items || []).map(item => ({
      ...item,
      product_name: item.product_name || getProductName(item.product_id)
    }));
    
    try {
      // Create a modified order with the custom ID if needed
      const orderToUse = useCustomId && customInvoiceId 
        ? { ...order, id: customInvoiceId }
        : order;
      
      // Generate the appropriate PDF based on type
      if (pendingPdfType === 'facture') {
        generateOrderInvoicePDF(orderToUse, clientName, enhancedItems, orderPayments, selectedPaymentMethod);
      } else if (pendingPdfType === 'ticket') {
        generateTicketPDF(orderToUse, clientName, enhancedItems, orderPayments);
      } else if (pendingPdfType === 'fiche') {
        generateFichePDF(orderToUse, clientName, enhancedItems, orderPayments);
      }
      
      // Close modal
      setShowInvoiceIdModal(false);
      setPendingInvoiceData(null);
      setCustomInvoiceId('');
      setUseCustomId(false);
      setSelectedPaymentMethod('');
    } catch {
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  const filteredOrders = useMemo(() => {
    const t = String(searchTerm || '').trim().toLowerCase();
    const tDigits = t.replace(/\D/g, '');
    const serverSearchActive = Boolean(t);

    return orders.filter((order) => {
      const clientName = order.client_name || getClientName(order.client_id);
      const clientPhone = String(order.client_phone || '');
      const clientPhoneDigits = clientPhone.replace(/\D/g, '');

      const matchesSearch =
        !t ||
        serverSearchActive ||
        String(clientName || '').toLowerCase().includes(t) ||
        String(order.id || '').includes(t) ||
        String(order.status || '').toLowerCase().includes(t) ||
        String(order.doctor_name || '').toLowerCase().includes(t) ||
        clientPhone.toLowerCase().includes(t) ||
        (tDigits.length > 0 && clientPhoneDigits.includes(tDigits));

      if (!matchesSearch) return false;

      if (listFilters.status !== 'all' && normalizeLegacyStatus(order.status) !== listFilters.status) return false;

      const verreVal = order.verre_verifie == null || order.verre_verifie === '' ? 'empty' : String(order.verre_verifie);
      if (listFilters.verre !== 'all' && verreVal !== listFilters.verre) return false;

      const coupureVal = order.coupure == null || order.coupure === '' ? 'empty' : String(order.coupure);
      if (listFilters.coupure !== 'all' && coupureVal !== listFilters.coupure) return false;

      if (listFilters.payment !== 'all') {
        const total = getOrderTotal(order);
        const paid = getTotalPaid(order);
        const paymentState = paid <= 0 ? 'unpaid' : paid < total ? 'partial' : 'paid';
        if (paymentState !== listFilters.payment) return false;
      }

      if (listFilters.doctorId !== 'all' && String(order.doctor_id || '') !== String(listFilters.doctorId)) {
        return false;
      }

      if (listFilters.employeeId !== 'all') {
        const orderEmployeeId = order.employee_id != null ? String(order.employee_id) : '';
        if (orderEmployeeId !== String(listFilters.employeeId)) return false;
      }

      if (listFilters.lensType !== 'all') {
        const hasLenses = orderHasLentilles(order);
        if (listFilters.lensType === 'with_lenses' && !hasLenses) return false;
        if (listFilters.lensType === 'without_lenses' && hasLenses) return false;
      }

      if (listFilters.paymentMode !== 'all') {
        const payments = Array.isArray(order.payments) ? order.payments : [];
        const hasMode = payments.some((p) => String(p.mode || '') === listFilters.paymentMode);
        if (!hasMode) return false;
      }

      if (listFilters.dateFrom || listFilters.dateTo) {
        const orderDate = new Date(order.created_at);
        if (!Number.isNaN(orderDate.getTime())) {
          if (listFilters.dateFrom) {
            const from = new Date(`${listFilters.dateFrom}T00:00:00`);
            if (orderDate < from) return false;
          }
          if (listFilters.dateTo) {
            const to = new Date(`${listFilters.dateTo}T23:59:59`);
            if (orderDate > to) return false;
          }
        }
      }

      return true;
    });
  }, [orders, searchTerm, listFilters, getClientName, getOrderTotal, getTotalPaid]);

  const ordersCommandesView = useMemo(
    () =>
      filteredOrders.filter(
        (o) => !orderIsLentillesOnlySection(o) && !isCommandeEnLigne(o)
      ),
    [filteredOrders]
  );
  const ordersLentillesView = useMemo(
    () =>
      filteredOrders.filter(
        (o) => orderIsLentillesOnlySection(o) && !isCommandeEnLigne(o)
      ),
    [filteredOrders]
  );
  const ordersEnLigneView = useMemo(
    () => filteredOrders.filter((o) => isCommandeEnLigne(o)),
    [filteredOrders]
  );

  const pdfPortalOrder = useMemo(
    () =>
      pdfDropdownOpen != null
        ? filteredOrders.find((o) => o.id === pdfDropdownOpen) ||
          orders.find((o) => o.id === pdfDropdownOpen)
        : null,
    [pdfDropdownOpen, filteredOrders, orders]
  );

  const clientOptions = useMemo(
    () =>
      clients.map((client) => ({
        value: client.id.toString(),
        label: client.name
      })),
    [clients]
  );

  const doctorOptions = useMemo(
    () =>
      doctors.map((doctor) => ({
        value: doctor.id.toString(),
        label: doctor.name
      })),
    [doctors]
  );

  /** Employees who created at least one loaded order (for "Créé par" filter) */
  const employeeFilterOptions = useMemo(() => {
    const byId = new Map();
    for (const order of orders) {
      if (order.employee_id == null) continue;
      const id = String(order.employee_id);
      if (byId.has(id)) continue;
      const name = String(order.employee_name || '').trim();
      byId.set(id, { value: id, label: name || `Employé #${id}` });
    }
    return Array.from(byId.values()).sort((a, b) =>
      a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' })
    );
  }, [orders]);

  const insuranceOptions = [
    { value: '', label: 'Aucune' },
    { value: 'CNSS', label: 'CNSS' },
    { value: 'CNOPS', label: 'CNOPS' },
    { value: 'Autre', label: 'Autre' }
  ];

  const statusOptions = SALES_ORDER_STATUS_OPTIONS;

  // Helper: options for Select
  const frameOptions = useMemo(
    () =>
      products
        .filter(
          (p) =>
            p.type === 'frames' ||
            (p.category_name && p.category_name.toLowerCase().includes('monture'))
        )
        .map((p) => ({
          value: String(p.id),
          label: `${p.name}${p.brand ? ' - ' + p.brand : ''}${p.model ? ' (' + p.model + ')' : ''}`
        })),
    [products]
  );
  const lensOptions = useMemo(
    () =>
      products
        .filter(
          (p) =>
            p.type === 'glasses' ||
            (p.category_name && p.category_name.toLowerCase().includes('verre'))
        )
        .map((p) => ({
          value: String(p.id),
          label: `${p.name}${p.brand ? ' - ' + p.brand : ''}${p.model ? ' (' + p.model + ')' : ''}`
        })),
    [products]
  );
  const accessoryOptions = useMemo(
    () =>
      products
        .filter(
          (p) =>
            p.type === 'accessories' ||
            (p.category_name && p.category_name.toLowerCase().includes('accessoire'))
        )
        .map((p) => ({
          value: String(p.id),
          label: `${p.name}${p.brand ? ' - ' + p.brand : ''}${p.model ? ' (' + p.model + ')' : ''}`
        })),
    [products]
  );

  const updateOrderPaymentsInList = useCallback((orderId, newPayments) => {
    setOrders(prevOrders => prevOrders.map(order =>
      order.id === orderId ? { ...order, payments: newPayments } : order
    ));
  }, []);

  const handlePatchStatus = useCallback(async (orderId, status) => {
    setStatusSavingId(orderId);
    try {
      const { data } = await salesOrdersAPI.patchSalesOrderStatus(orderId, { status });
      setOrders((prev) =>
        prev.map((o) => (o.id === orderId ? { ...o, status: data.status } : o))
      );
      toast.success('Statut mis à jour');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors de la mise à jour du statut');
    } finally {
      setStatusSavingId(null);
    }
  }, []);

  const handlePatchVerreTracking = useCallback(async (orderId, patch) => {
    setVerreSavingId(orderId);
    try {
      const { data } = await salesOrdersAPI.patchVerreTracking(orderId, patch);
      setOrders((prev) =>
        prev.map((o) =>
          o.id === orderId
            ? { ...o, verre_verifie: data.verre_verifie ?? null, coupure: data.coupure ?? null }
            : o
        )
      );
      toast.success('Enregistré');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors de la mise à jour');
    } finally {
      setVerreSavingId(null);
    }
  }, []);

  /**
   * Stable column definitions — transient states (busy indicators, open dropdowns) are
   * consumed from OrderListCtx inside each cell component, so they don't appear in deps
   * and the entire table never re-renders just because one row is saving.
   */
  const tableColumns = useMemo(() => {
    if (loading) return [];
    return [
      {
        key: 'id',
        label: 'N°',
        headerClassName: 'w-[4.5rem]',
        className: '!whitespace-normal align-top',
        render: (value) => (
          <span className="font-semibold text-neutral-900">#{value}</span>
        )
      },
      {
        key: 'client_id',
        label: 'Client',
        headerClassName: 'min-w-[8rem]',
        className: '!whitespace-normal align-top',
        render: (value, order) => (
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 min-w-0">
              <p className="font-medium text-neutral-900 truncate">{getClientName(value, order.client_name)}</p>
              <OrderVilleTag order={order} className="shrink-0" />
            </div>
            <p className="text-xs text-neutral-500 truncate">{order.client_phone || '-'}</p>
          </div>
        )
      },
      {
        key: 'status',
        label: 'Statut',
        headerClassName: 'min-w-[8rem]',
        className: '!whitespace-normal align-top',
        render: (_v, order) => (
          <div className="py-0.5">
            <OrderStatusListCell order={order} />
          </div>
        )
      },
      {
        key: 'verre_coupure',
        label: 'Verre / Coupure',
        headerClassName: 'min-w-[calc(9rem-5px)]',
        className: '!whitespace-normal align-top',
        render: (_v, order) => <OrderVerreCoupureCell order={order} />
      },
      {
        key: 'amounts',
        label: 'Montants',
        headerClassName: 'min-w-[7rem]',
        className: '!whitespace-normal align-top',
        render: (_v, order) => {
          const total = getOrderTotal(order);
          const paid = getTotalPaid(order);
          const rest = Math.max(0, total - paid);
          const paidColor = paid === 0 ? 'text-danger-600' : paid < total ? 'text-warning-700' : 'text-success-700';
          const restColor = rest > 0 ? 'text-danger-600' : 'text-success-700';
          return (
            <div className="space-y-0.5 text-xs leading-snug">
              <div>
                <span className="text-neutral-500">Total </span>
                <span className="font-semibold text-neutral-800">{formatCurrency(total)}</span>
              </div>
              <div>
                <span className="text-neutral-500">Payé </span>
                <span className={`font-semibold ${paidColor}`}>{formatCurrency(paid)}</span>
              </div>
              <div>
                <span className="text-neutral-500">Reste </span>
                <span className={`font-semibold ${restColor}`}>{formatCurrency(rest)}</span>
              </div>
            </div>
          );
        }
      },
      {
        key: 'created_at',
        label: 'Date / Créé par',
        headerClassName: 'min-w-[7rem]',
        className: '!whitespace-normal align-top',
        render: (value, order) => (
          <div className="space-y-1 text-xs leading-snug">
            <div className="flex items-center gap-1 min-w-0">
              <Calendar className="w-3 h-3 text-neutral-400 shrink-0" />
              <span className="text-neutral-900">{formatDateOnlyFr(value)}</span>
            </div>
            <div className="flex items-center gap-1 min-w-0">
              <Contact className="w-3 h-3 text-neutral-400 shrink-0" />
              <span className="text-neutral-600 truncate" title={order.employee_name || ''}>
                {order.employee_name?.trim() || '—'}
              </span>
            </div>
          </div>
        )
      },
      {
        key: 'actions',
        label: 'Actions',
        headerClassName: 'w-[10.5rem] min-w-[10.5rem] actions-cell',
        className: '!whitespace-nowrap align-middle actions-cell',
        render: (_v, order) => <OrderActionsCell order={order} />,
      }
    ];
  }, [loading, getClientName, getOrderTotal, getTotalPaid]);

  // Close PDF dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        pdfDropdownOpen &&
        !event.target.closest('.pdf-dropdown-container') &&
        !event.target.closest('.pdf-dropdown-portal')
      ) {
        setPdfDropdownOpen(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [pdfDropdownOpen]);

  useLayoutEffect(() => {
    if (!pdfDropdownOpen) {
      pdfAnchorRef.current = null;
      setPdfMenuPos(null);
      return;
    }
    const sync = () => {
      const el = pdfAnchorRef.current;
      if (!el) return;
      setPdfMenuPos(computePdfMenuPosition(el.getBoundingClientRect()));
    };
    sync();
    const onScrollOrResize = () => sync();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [pdfDropdownOpen]);

  const enCoursOrders = useMemo(
    () => orders.filter((o) => normalizeLegacyStatus(o.status) === 'en_cours').length,
    [orders]
  );
  const livreOrders = useMemo(
    () => orders.filter((o) => LIVRE_STATUS_SET.has(normalizeLegacyStatus(o.status))).length,
    [orders]
  );
  const annuleOrders = useMemo(
    () => orders.filter((o) => normalizeLegacyStatus(o.status) === 'annule').length,
    [orders]
  );

  // Reset to page 1 when search/filter/tab changes
  useEffect(() => { setCurrentPage(1); }, [searchTerm, lentillesView, listFilters]);

  const activeList =
    lentillesView === 'commandes'
      ? ordersCommandesView
      : lentillesView === 'commandes_lentilles'
        ? ordersLentillesView
        : ordersEnLigneView;
  const totalPages  = Math.ceil(activeList.length / PAGE_SIZE);
  const paginatedList = useMemo(
    () => activeList.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [activeList, currentPage]
  );

  /** Context value — only recreated when a stable handler or transient state changes. */
  const ctxValue = useMemo(() => ({
    statusSavingId,
    verreSavingId,
    openingOrderId,
    sendingWaOrderId,
    pdfDropdownOpen,
    setPdfDropdownOpen,
    setPdfMenuPos,
    pdfAnchorRef,
    handlePatchStatus,
    handlePatchVerreTracking,
    handleEdit,
    loadOrderPayments,
    handleSendOrderValidationWhatsApp,
    handleDelete,
    generateInvoicePDF,
    generateTicketPDFForOrder,
    generateFichePDFForOrder,
    getOrderTotal,
    getTotalPaid,
    getClientName,
  }), [
    statusSavingId, verreSavingId, openingOrderId, sendingWaOrderId, pdfDropdownOpen,
    setPdfDropdownOpen, setPdfMenuPos,
    handlePatchStatus, handlePatchVerreTracking,
    handleEdit, loadOrderPayments, handleSendOrderValidationWhatsApp, handleDelete,
    generateInvoicePDF, generateTicketPDFForOrder, generateFichePDFForOrder,
    getOrderTotal, getTotalPaid, getClientName,
  ]);

  const opticalModalExpanded = isOpticalModalExpanded(modalType, {
    lensSelectionType,
    modalForm,
    leftLensProduct,
    rightLensProduct,
    isEdit: modalEditIdx !== null,
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ModernLoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="commandes-page space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-neutral-900 sm:text-2xl">Commandes (Ventes)</h1>
          <p className="text-sm text-neutral-600">Gérez les commandes de vente</p>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
          <ModernSearchBar
            placeholder="Rechercher des commandes..."
            onSearch={setSearchTerm}
            className="w-full sm:w-64"
            debounce={300}
          />
          <ModernButton
            variant="primary"
            icon={<Plus className="w-4 h-4" />}
            onClick={handleAdd}
            className="w-full sm:w-auto"
          >
            Nouvelle commande
          </ModernButton>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4 lg:gap-6">
        <ModernCard className="p-3 sm:p-6">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-xs font-medium text-neutral-600 sm:text-sm">Total Commandes</p>
              <p className="text-xl font-bold text-neutral-900 sm:text-2xl">{orders.length}</p>
            </div>
            <div className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-100 sm:flex sm:h-12 sm:w-12">
              <ShoppingCart className="w-5 h-5 text-primary-600 sm:w-6 sm:h-6" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-3 sm:p-6">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-xs font-medium text-neutral-600 sm:text-sm">En cours</p>
              <p className="text-xl font-bold text-warning-600 sm:text-2xl">{enCoursOrders}</p>
            </div>
            <div className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-warning-100 sm:flex sm:h-12 sm:w-12">
              <Clock className="w-5 h-5 text-warning-600 sm:w-6 sm:h-6" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-3 sm:p-6">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-xs font-medium text-neutral-600 sm:text-sm">Livrées</p>
              <p className="text-xl font-bold text-success-600 sm:text-2xl">{livreOrders}</p>
            </div>
            <div className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-success-100 sm:flex sm:h-12 sm:w-12">
              <Truck className="w-5 h-5 text-success-600 sm:w-6 sm:h-6" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-3 sm:p-6">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-xs font-medium text-neutral-600 sm:text-sm">Annulées</p>
              <p className="text-xl font-bold text-danger-600 sm:text-2xl">{annuleOrders}</p>
            </div>
            <div className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-danger-100 sm:flex sm:h-12 sm:w-12">
              <XCircle className="w-5 h-5 text-danger-600 sm:w-6 sm:h-6" />
            </div>
          </div>
        </ModernCard>
      </div>

      {/* Orders Table */}
      <ModernCard className="p-3 sm:p-6">
        <div className="mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-neutral-900 sm:text-lg">Liste des commandes</h3>
            <p className="text-xs text-neutral-500 sm:text-sm">
              {filteredOrders.length === 0 ? '0 commande' : `${filteredOrders.length} commande${filteredOrders.length > 1 ? 's' : ''}`}
              <span className="hidden sm:inline">
                {filteredOrders.length > 0 &&
                  ` · ${ordersCommandesView.length} magasin · ${ordersLentillesView.length} lentilles · ${ordersEnLigneView.length} en ligne`}
              </span>
              {totalPages > 1 && ` · page ${currentPage}/${totalPages}`}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
            <ModernButton
              variant={showFilters ? 'secondary' : 'ghost'}
              size="sm"
              icon={<Filter className="w-4 h-4" />}
              onClick={() => setShowFilters((v) => !v)}
              className="w-full sm:w-auto"
            >
              {showFilters ? 'Masquer filtres' : 'Filtres'}
            </ModernButton>
            <ModernButton
              variant="primary"
              size="sm"
              icon={<Plus className="w-4 h-4" />}
              onClick={handleAdd}
              className="hidden w-full sm:inline-flex sm:w-auto"
            >
              Nouvelle commande
            </ModernButton>
            <ModernButton
              variant="ghost"
              size="sm"
              icon={<Download className="w-4 h-4" />}
              onClick={handleExportPayments}
              className="col-span-2 w-full sm:col-auto sm:w-auto"
            >
              Export CSV
            </ModernButton>
          </div>
        </div>

        <div className="mb-4 sm:mb-6" role="tablist" aria-label="Filtrer par type de commande">
          
          <div className="flex flex-col gap-2 lg:flex-row lg:items-stretch lg:gap-3">
            <button
              type="button"
              role="tab"
              aria-selected={lentillesView === 'commandes'}
              onClick={() => setLentillesView('commandes')}
              title="Commandes magasin (hors lentilles seules et hors commandes en ligne)"
              className={`flex flex-1 items-center justify-center gap-2 rounded-full px-3 py-2.5 text-sm font-bold transition-all sm:min-h-[3.25rem] sm:px-4 sm:py-3 ${
                lentillesView === 'commandes'
                  ? 'bg-gradient-to-r from-blue-600 via-sky-500 to-teal-500 text-white shadow-md'
                  : 'border border-neutral-200 bg-neutral-100 text-neutral-800 hover:bg-neutral-200/90'
              }`}
            >
              <ShoppingCart className="h-4 w-4 shrink-0 opacity-95 sm:h-5 sm:w-5" aria-hidden />
              <span className="flex items-center gap-2">
                <span className="sm:hidden">Magasin</span>
                <span className="hidden sm:inline">Commandes</span>
                <span
                  className={`inline-flex min-h-[1.5rem] min-w-[1.5rem] items-center justify-center rounded-full px-2 text-xs font-bold tabular-nums ${
                    lentillesView === 'commandes'
                      ? 'bg-white/25 text-white ring-1 ring-white/30'
                      : 'bg-neutral-200/90 text-neutral-900'
                  }`}
                  aria-label={`${ordersCommandesView.length} commandes`}
                >
                  {ordersCommandesView.length}
                </span>
              </span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={lentillesView === 'commandes_lentilles'}
              onClick={() => setLentillesView('commandes_lentilles')}
              title="Lentilles magasin seules ou avec accessoires (sans monture ni verre, hors en ligne)"
              className={`flex flex-1 items-center justify-center gap-2 rounded-full px-3 py-2.5 text-sm font-bold transition-all sm:min-h-[3.25rem] sm:px-4 sm:py-3 ${
                lentillesView === 'commandes_lentilles'
                  ? 'bg-gradient-to-r from-blue-600 via-sky-500 to-teal-500 text-white shadow-md'
                  : 'border border-neutral-200 bg-neutral-100 text-neutral-800 hover:bg-neutral-200/90'
              }`}
            >
              <Contact className="h-4 w-4 shrink-0 opacity-95 sm:h-5 sm:w-5" aria-hidden />
              <span className="flex items-center gap-2">
                <span className="sm:hidden">Lentilles</span>
                <span className="hidden sm:inline">Commandes lentilles</span>
                <span
                  className={`inline-flex min-h-[1.5rem] min-w-[1.5rem] items-center justify-center rounded-full px-2 text-xs font-bold tabular-nums ${
                    lentillesView === 'commandes_lentilles'
                      ? 'bg-white/25 text-white ring-1 ring-white/30'
                      : 'bg-neutral-200/90 text-neutral-900'
                  }`}
                  aria-label={`${ordersLentillesView.length} commandes lentilles`}
                >
                  {ordersLentillesView.length}
                </span>
              </span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={lentillesView === 'commandes_en_ligne'}
              onClick={() => setLentillesView('commandes_en_ligne')}
              title="Commandes passées en ligne (hors onglets magasin)"
              className={`flex flex-1 items-center justify-center gap-2 rounded-full px-3 py-2.5 text-sm font-bold transition-all sm:min-h-[3.25rem] sm:px-4 sm:py-3 ${
                lentillesView === 'commandes_en_ligne'
                  ? 'bg-gradient-to-r from-blue-600 via-sky-500 to-teal-500 text-white shadow-md'
                  : 'border border-neutral-200 bg-neutral-100 text-neutral-800 hover:bg-neutral-200/90'
              }`}
            >
              <Globe className="h-4 w-4 shrink-0 opacity-95 sm:h-5 sm:w-5" aria-hidden />
              <span className="flex items-center gap-2">
                <span className="sm:hidden">En ligne</span>
                <span className="hidden sm:inline">Commandes en ligne</span>
                <span
                  className={`inline-flex min-h-[1.5rem] min-w-[1.5rem] items-center justify-center rounded-full px-2 text-xs font-bold tabular-nums ${
                    lentillesView === 'commandes_en_ligne'
                      ? 'bg-white/25 text-white ring-1 ring-white/30'
                      : 'bg-neutral-200/90 text-neutral-900'
                  }`}
                  aria-label={`${ordersEnLigneView.length} commandes en ligne`}
                >
                  {ordersEnLigneView.length}
                </span>
              </span>
            </button>
          </div>
        </div>

        {showFilters && (
        <div className="mb-4 grid grid-cols-1 gap-2 sm:mb-5 sm:grid-cols-2 sm:gap-3 xl:grid-cols-5">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Rechercher: nom client, téléphone, n° commande..."
            className="h-10 rounded-xl border border-neutral-200 bg-white px-3 text-sm font-medium text-neutral-700 focus:outline-none focus:ring-2 focus:ring-primary-400/35 xl:col-span-2"
            aria-label="Recherche commandes par nom ou téléphone"
          />

          <select
            value={listFilters.status}
            onChange={(e) => setListFilters((f) => ({ ...f, status: e.target.value }))}
            className="h-10 rounded-xl border border-neutral-200 bg-white px-3 text-sm font-medium text-neutral-700 focus:outline-none focus:ring-2 focus:ring-primary-400/35"
            aria-label="Filtre statut"
          >
            <option value="all">Statut: Tous</option>
            {SALES_ORDER_STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          <select
            value={listFilters.verre}
            onChange={(e) => setListFilters((f) => ({ ...f, verre: e.target.value }))}
            className="h-10 rounded-xl border border-neutral-200 bg-white px-3 text-sm font-medium text-neutral-700 focus:outline-none focus:ring-2 focus:ring-primary-400/35"
            aria-label="Filtre verre"
          >
            <option value="all">Verre: Tous</option>
            <option value="empty">Verre: Vide</option>
            <option value="oui">Verre: Oui</option>
            <option value="non">Verre: Non</option>
          </select>

          <select
            value={listFilters.coupure}
            onChange={(e) => setListFilters((f) => ({ ...f, coupure: e.target.value }))}
            className="h-10 rounded-xl border border-neutral-200 bg-white px-3 text-sm font-medium text-neutral-700 focus:outline-none focus:ring-2 focus:ring-primary-400/35"
            aria-label="Filtre coupure"
          >
            <option value="all">Coupure: Tous</option>
            <option value="empty">Coupure: Vide</option>
            {COUPURE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                Coupure: {opt.label}
              </option>
            ))}
          </select>

          <select
            value={listFilters.payment}
            onChange={(e) => setListFilters((f) => ({ ...f, payment: e.target.value }))}
            className="h-10 rounded-xl border border-neutral-200 bg-white px-3 text-sm font-medium text-neutral-700 focus:outline-none focus:ring-2 focus:ring-primary-400/35"
            aria-label="Filtre paiement"
          >
            <option value="all">Paiement: Tous</option>
            <option value="unpaid">Paiement: Non payé</option>
            <option value="partial">Paiement: Partiel</option>
            <option value="paid">Paiement: Payé</option>
          </select>

          <select
            value={listFilters.doctorId}
            onChange={(e) => setListFilters((f) => ({ ...f, doctorId: e.target.value }))}
            className="h-10 rounded-xl border border-neutral-200 bg-white px-3 text-sm font-medium text-neutral-700 focus:outline-none focus:ring-2 focus:ring-primary-400/35"
            aria-label="Filtre médecin"
          >
            <option value="all">Médecin: Tous</option>
            {doctors.map((d) => (
              <option key={d.id} value={String(d.id)}>{d.name}</option>
            ))}
          </select>

          <select
            value={listFilters.employeeId}
            onChange={(e) => setListFilters((f) => ({ ...f, employeeId: e.target.value }))}
            className="h-10 rounded-xl border border-neutral-200 bg-white px-3 text-sm font-medium text-neutral-700 focus:outline-none focus:ring-2 focus:ring-primary-400/35"
            aria-label="Filtre créé par employé"
          >
            <option value="all">Créé par: Tous</option>
            {employeeFilterOptions.map((emp) => (
              <option key={emp.value} value={emp.value}>{emp.label}</option>
            ))}
          </select>

          <select
            value={listFilters.lensType}
            onChange={(e) => setListFilters((f) => ({ ...f, lensType: e.target.value }))}
            className="h-10 rounded-xl border border-neutral-200 bg-white px-3 text-sm font-medium text-neutral-700 focus:outline-none focus:ring-2 focus:ring-primary-400/35"
            aria-label="Filtre type lentilles"
          >
            <option value="all">Type: Tous</option>
            <option value="with_lenses">Avec lentilles</option>
            <option value="without_lenses">Sans lentilles</option>
          </select>

          <select
            value={listFilters.paymentMode}
            onChange={(e) => setListFilters((f) => ({ ...f, paymentMode: e.target.value }))}
            className="h-10 rounded-xl border border-neutral-200 bg-white px-3 text-sm font-medium text-neutral-700 focus:outline-none focus:ring-2 focus:ring-primary-400/35"
            aria-label="Filtre mode de paiement"
          >
            <option value="all">Mode paiement: Tous</option>
            <option value="espece">Espèce</option>
            <option value="cheque">Chèque</option>
            <option value="carte_bancaire">Carte bancaire</option>
            <option value="virement">Virement</option>
          </select>

          <input
            type="date"
            value={listFilters.dateFrom}
            onChange={(e) => setListFilters((f) => ({ ...f, dateFrom: e.target.value }))}
            className="h-10 rounded-xl border border-neutral-200 bg-white px-3 text-sm font-medium text-neutral-700 focus:outline-none focus:ring-2 focus:ring-primary-400/35"
            aria-label="Filtre date début"
          />

          <input
            type="date"
            value={listFilters.dateTo}
            onChange={(e) => setListFilters((f) => ({ ...f, dateTo: e.target.value }))}
            className="h-10 rounded-xl border border-neutral-200 bg-white px-3 text-sm font-medium text-neutral-700 focus:outline-none focus:ring-2 focus:ring-primary-400/35"
            aria-label="Filtre date fin"
          />

          <ModernButton
            type="button"
            variant="ghost"
            onClick={() =>
              setListFilters({
                status: 'all',
                verre: 'all',
                coupure: 'all',
                payment: 'all',
                doctorId: 'all',
                employeeId: 'all',
                lensType: 'all',
                paymentMode: 'all',
                dateFrom: '',
                dateTo: '',
              })
            }
            className="w-full sm:w-auto"
          >
            Réinitialiser filtres
          </ModernButton>
        </div>
        )}

        <OrderListCtx.Provider value={ctxValue}>
          <div>
            {activeList.length === 0 ? (
              <p className="rounded-xl border border-dashed border-neutral-200 bg-neutral-50/80 px-4 py-8 text-center text-sm text-neutral-500">
                Aucune commande pour la recherche actuelle.
              </p>
            ) : (
              <>
                {/* Mobile cards */}
                <div className="space-y-3.5 lg:hidden">
                  {paginatedList.map((order) => (
                    <OrderMobileCard key={order.id} order={order} />
                  ))}
                </div>

                {/* Desktop table */}
                <div className="hidden lg:block">
                  <ModernTable
                    columns={tableColumns}
                    data={paginatedList}
                    className="w-full table-fixed commandes-list-table"
                  />
                </div>

                {/* Pagination controls */}
                {totalPages > 1 && (
                  <div className="mt-4 flex flex-col gap-3 border-t border-neutral-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-center text-sm text-neutral-500 sm:text-left">
                      {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, activeList.length)} sur {activeList.length}
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-1.5">
                      <button
                        type="button"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-neutral-200 bg-white text-sm font-medium text-neutral-700 shadow-sm transition hover:bg-neutral-50 disabled:pointer-events-none disabled:opacity-40"
                      >
                        ‹
                      </button>
                      {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                        let page;
                        if (totalPages <= 7) { page = i + 1; }
                        else if (currentPage <= 4) { page = i + 1; if (i === 6) page = totalPages; }
                        else if (currentPage >= totalPages - 3) { page = i === 0 ? 1 : totalPages - 6 + i; }
                        else { const offsets = [1, 0, -1, 0, 1, 2, totalPages]; page = [1, currentPage - 1, currentPage, currentPage + 1, 0, 0, totalPages][i] || i; if (i === 4 || i === 5) return <span key={`ellipsis-${i}`} className="px-1 text-neutral-400">…</span>; }
                        return (
                          <button
                            key={page}
                            type="button"
                            onClick={() => setCurrentPage(page)}
                            className={`inline-flex h-9 min-w-[2.25rem] items-center justify-center rounded-lg border px-2 text-sm font-medium shadow-sm transition ${
                              page === currentPage
                                ? 'border-primary-500 bg-primary-500 text-white'
                                : 'border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      })}
                      <button
                        type="button"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-neutral-200 bg-white text-sm font-medium text-neutral-700 shadow-sm transition hover:bg-neutral-50 disabled:pointer-events-none disabled:opacity-40"
                      >
                        ›
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </OrderListCtx.Provider>
      </ModernCard>

      {/* Add/Edit Modal */}
      <ModernModal
        isOpen={showForm}
        onClose={() => {
          setShowForm(false);
          setEditOrderLoading(false);
          setDeletedPreviewMode(false);
        }}
        title={deletedPreviewMode ? `Détails commande supprimée #${editingId || ''}` : (editingId ? 'Modifier la commande' : 'Ajouter une commande')}
        size="xxl"
      >
        <div className="relative">
          {editOrderLoading && (
            <div
              className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/80 backdrop-blur-[1px]"
              aria-busy="true"
              aria-label="Chargement de la commande"
            >
              <ModernLoadingSpinner size="lg" />
            </div>
          )}
        {deletedPreviewMode && (
          <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Commande supprimée: mode lecture seule.
          </div>
        )}
        {formError && (
          <div className="text-red-600 font-semibold mb-2">{formError}</div>
        )}
        <form onSubmit={handleSubmit} className="space-y-6">
          <fieldset disabled={deletedPreviewMode} className={deletedPreviewMode ? 'opacity-90' : ''}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <EntitySelectWithAdd
              entityType="client"
              label="Client"
              options={clientOptions}
              value={form.client_id ? form.client_id.toString() : ''}
              onChange={value => setForm(f => ({ ...f, client_id: value }))}
              fetchOptions={fetchClients}
              required
              showAddButton={true}
            />
            {editingId && canEditCreator && !deletedPreviewMode && (
              <ModernSelect
                label="Créé par"
                name="employee_id"
                aria-label="Créé par"
                value={form.employee_id || null}
                options={creatorOptions}
                onChange={option => setForm(f => ({ ...f, employee_id: option ? option.value : '' }))}
                isClearable={false}
                isDisabled={editOrderLoading || submitLoading}
                placeholder="Sélectionner un créateur"
              />
            )}
            <ModernSelect
              label="Statut"
              name="status"
              value={statusOptions.find(opt => opt.value === form.status) || null}
              onChange={option => setForm(f => ({ ...f, status: option ? option.value : '' }))}
              options={statusOptions}
            />
            <ModernSelect
              label="Type de commande"
              name="commande_origine"
              value={
                COMMANDE_ORIGINE_OPTIONS.find((o) => o.value === form.commande_origine) ||
                COMMANDE_ORIGINE_OPTIONS[0]
              }
              onChange={(option) =>
                setForm((f) => ({
                  ...f,
                  commande_origine: option?.value || COMMANDE_ORIGINE_MAGASIN,
                }))
              }
              options={COMMANDE_ORIGINE_OPTIONS}
            />
          </div>

          {/* Doctor Assignment Section */}
          <div className="border-t pt-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Médecin</h3>
            
            {/* Only show checkbox when creating new order */}
            {!editingId && (
              <div className="flex items-center space-x-3 mb-4">
                <input
                  type="checkbox"
                  id="assignDoctor"
                  checked={assignDoctor}
                  onChange={(e) => {
                    setAssignDoctor(e.target.checked);
                    if (!e.target.checked) {
                      setForm(f => ({ ...f, doctor_id: '', doctor_name: '', doctor_phone: '' }));
                    }
                  }}
                  className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 focus:ring-2"
                />
                <label htmlFor="assignDoctor" className="text-sm font-medium text-gray-700">
                  Médecin à affecter
                </label>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ModernSelect
                label="Médecin"
                name="doctor_id"
                value={
                  doctorOptions.find(
                    (opt) => opt.value === (form.doctor_id ? form.doctor_id.toString() : '')
                  ) || null
                }
                onChange={option => {
                  setForm(f => ({ 
                    ...f, 
                    doctor_id: option ? option.value : '',
                    doctor_name: option ? option.label : '',
                    doctor_phone: '' // Reset phone when changing doctor
                  }));
                  // Automatically check the assignDoctor checkbox when a doctor is selected
                  if (option && !editingId) {
                    setAssignDoctor(true);
                  }
                }}
                options={doctorOptions}
                placeholder="Sélectionner un médecin..."
                isDisabled={false}
              />
              {form.doctor_name && (
                <div className="flex items-center space-x-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-blue-900">Médecin assigné</p>
                    <p className="text-sm text-blue-700">{form.doctor_name}</p>
                    {form.doctor_phone && (
                      <p className="text-xs text-blue-600">{form.doctor_phone}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Insurance Section */}
          <div className="border-t pt-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Mutuelle</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <ModernSelect
                label="Type de mutuelle"
                name="insurance"
                value={insuranceOptions.find(opt => opt.value === form.insurance) || null}
                onChange={option => {
                  const insuranceValue = option ? option.value : '';
                  setForm(f => ({ 
                    ...f, 
                    insurance: insuranceValue,
                    // Clear autre_mutuelle if not selecting "Autre"
                    autre_mutuelle: insuranceValue === 'Autre' ? f.autre_mutuelle : ''
                  }));
                }}
                options={insuranceOptions}
                placeholder="Sélectionner..."
              />
              {form.insurance === 'Autre' && (
                <ModernInput
                  label="Autre mutuelle"
                  name="autre_mutuelle"
                  type="text"
                  value={form.autre_mutuelle}
                  onChange={(e) => setForm(f => ({ ...f, autre_mutuelle: e.target.value }))}
                  placeholder="Saisir le nom de la mutuelle"
                />
              )}
              {editingId && (
                <ModernInput
                  label="Date de commande"
                  name="order_date"
                  type="date"
                  value={form.order_date}
                  onChange={(e) => setForm(f => ({ ...f, order_date: e.target.value }))}
                  icon={<Calendar className="w-5 h-5" />}
                />
              )}
              <ModernInput
                label="Date de réception"
                name="date_reception"
                type="date"
                value={form.date_reception}
                onChange={(e) => setForm(f => ({ ...f, date_reception: e.target.value }))}
                icon={<Calendar className="w-5 h-5" />}
              />
            </div>
          </div>

          <div className="border-t pt-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Note commande</h3>
            <textarea
              name="order_note"
              value={form.order_note || ''}
              onChange={(e) => setForm((f) => ({ ...f, order_note: e.target.value }))}
              placeholder="Ajouter une note générale pour cette commande..."
              rows={3}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            />
            <p className="mt-1 text-xs text-neutral-500">
              Cette note concerne toute la commande. Les remarques par produit (verres, etc.) restent inchangées.
            </p>
          </div>

          {/* Product selection section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-t pt-4 mt-4">
            <div className="font-semibold text-primary-700 md:col-span-2">Produits</div>
            <ModernCard className="mb-6 p-4 shadow-lg">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-primary-700">Montures</h3>
                <ModernButton variant="primary" size="sm" onClick={(e) => { e.preventDefault(); e.stopPropagation(); openModal('monture'); }}>
                  <div className="flex items-center">
                    <Plus className="w-4 h-4" />
                    <span className="ml-2">Ajouter</span>
                  </div>
                </ModernButton>
              </div>
              <div className="flex flex-wrap gap-2">
                {form.items.filter(i => i.item_type === 'monture').map((item, idx) => (
                  <div key={idx} className="flex items-center bg-primary-50 rounded-full px-4 py-2 shadow-sm border border-primary-200">
                    <span className="font-medium mr-2">{getProductName(item.product_id)}</span>
                    <span className="mx-2 text-neutral-500">x{item.quantity}</span>
                    <span className="text-primary-700 font-semibold mr-2">{formatCurrency(itemPrice(item.price, item.product_id))}</span>
                    <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); openModal('monture', idx); }} className="ml-1 text-blue-500 hover:text-blue-700"><Edit className="w-4 h-4" /></button>
                    <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeItem(idx); }} className="ml-1 text-red-500 hover:text-red-700"><X className="w-4 h-4" /></button>
                  </div>
                ))}
                {form.items.filter(i => i.item_type === 'monture').length === 0 && (
                  <span className="text-neutral-400">Aucune monture ajoutée</span>
                )}
              </div>
            </ModernCard>
            <ModernCard className="mb-6 p-4 shadow-lg">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-primary-700">Verres</h3>
                <div className="flex items-center space-x-2">
                  <ModernButton variant="primary" size="sm" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setLensSelectionTarget('verre'); setLensSelectionModal(true); }}>
                    <div className="flex items-center">
                      <Plus className="w-4 h-4" />
                      <span className="ml-2">Ajouter</span>
                    </div>
                  </ModernButton>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {(() => {
                  const verres = form.items.filter(i => i.item_type === 'verre');
                  
                  // If no verres, show empty state
                  if (verres.length === 0) {
                    return <span className="text-neutral-400">Aucun verre ajouté</span>;
                  }
                  
                  // Group verres by product_id
                  const verresByProduct = {};
                  verres.forEach(item => {
                    if (!verresByProduct[item.product_id]) {
                      verresByProduct[item.product_id] = [];
                    }
                    verresByProduct[item.product_id].push(item);
                  });
                  
                  return Object.entries(verresByProduct).map(([productId, items]) => {
                    const leftLens = items.find(item => item.side === 'left');
                    const rightLens = items.find(item => item.side === 'right');
                    const productName = getProductName(productId);
                    // Show all lenses with proper naming and totals
                    if (leftLens && rightLens) {
                      const idxLeft = form.items.findIndex((it) => it === leftLens);
                      const idxRight = form.items.findIndex((it) => it === rightLens);
                      const leftCorr = formatOpticalCorrectionSummary(leftLens);
                      const rightCorr = formatOpticalCorrectionSummary(rightLens);
                      return (
                        <React.Fragment key={productId}>
                          <div className="flex items-center flex-wrap gap-1 bg-blue-50 rounded-full px-4 py-2 shadow-sm border border-blue-200">
                            <span className="font-medium mr-1">{productName}</span>
                            <span className="text-neutral-500">Gauche</span>
                            {leftCorr && <span className="mx-1 text-xs text-blue-600">{leftCorr}</span>}
                            <span className="mx-1 text-neutral-500">x{leftLens.quantity}</span>
                            <span className="text-blue-700 font-semibold mr-1">
                              {formatCurrency(itemPrice(leftLens.price, leftLens.product_id) * Number(leftLens.quantity))}
                            </span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                openModal('verre', idxLeft);
                              }}
                              className="ml-1 text-blue-500 hover:text-blue-700"
                              title="Modifier le verre gauche"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (idxLeft !== -1) removeItem(idxLeft);
                              }}
                              className="ml-0.5 text-red-500 hover:text-red-700"
                              title="Retirer le verre gauche"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="flex items-center flex-wrap gap-1 bg-blue-50 rounded-full px-4 py-2 shadow-sm border border-blue-200">
                            <span className="font-medium mr-1">{productName}</span>
                            <span className="text-neutral-500">Droit</span>
                            {rightCorr && <span className="mx-1 text-xs text-blue-600">{rightCorr}</span>}
                            <span className="mx-1 text-neutral-500">x{rightLens.quantity}</span>
                            <span className="text-blue-700 font-semibold mr-1">
                              {formatCurrency(itemPrice(rightLens.price, rightLens.product_id) * Number(rightLens.quantity))}
                            </span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                openModal('verre', idxRight);
                              }}
                              className="ml-1 text-blue-500 hover:text-blue-700"
                              title="Modifier le verre droit"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (idxRight !== -1) removeItem(idxRight);
                              }}
                              className="ml-0.5 text-red-500 hover:text-red-700"
                              title="Retirer le verre droit"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </React.Fragment>
                      );
                    } else {
                      // Show individual lenses
                      return items.map((item, idx) => {
                        const itemPriceVal = itemPrice(item.price, item.product_id);
                        const itemTotal = itemPriceVal * Number(item.quantity);
                        const itemIdx = form.items.findIndex(it => it === item);
                        const corr = formatOpticalCorrectionSummary(item);
                        return (
                          <div key={`${productId}-${item.side}`} className="flex items-center bg-blue-50 rounded-full px-4 py-2 shadow-sm border border-blue-200">
                            <span className="font-medium mr-2">{productName}</span>
                            <span className="mx-2 text-neutral-500">{item.side === 'left' ? 'Gauche' : item.side === 'right' ? 'Droit' : ''}</span>
                            {corr && <span className="mx-1 text-xs text-blue-600">{corr}</span>}
                            <span className="mx-2 text-neutral-500">x{item.quantity}</span>
                            <span className="text-blue-700 font-semibold mr-2">{formatCurrency(itemTotal)}</span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (itemIdx !== -1) openModal('verre', itemIdx);
                              }}
                              className="ml-1 text-blue-500 hover:text-blue-700"
                              title="Modifier ce verre"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button 
                              onClick={(e) => { 
                                e.preventDefault(); 
                                e.stopPropagation(); 
                                if (itemIdx !== -1) removeItem(itemIdx);
                              }} 
                              className="ml-1 text-red-500 hover:text-red-700"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      });
                    }
                  });
                })()}
              </div>
            </ModernCard>
            <ModernCard className="mb-6 p-4 shadow-lg">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-primary-700">Accessoires</h3>
                <ModernButton variant="primary" size="sm" onClick={(e) => { e.preventDefault(); e.stopPropagation(); openModal('accessoire'); }}>
                  <div className="flex items-center">
                    <Plus className="w-4 h-4" />
                    <span className="ml-2">Ajouter</span>
                  </div>
                </ModernButton>
              </div>
              <div className="flex flex-wrap gap-2">
                {form.items.filter(i => i.item_type === 'accessoire').map((item) => {
                  const fullIdx = form.items.findIndex(it => it === item);
                  return (
                    <div key={fullIdx} className="flex items-center bg-green-50 rounded-full px-4 py-2 shadow-sm border border-green-200">
                      <span className="font-medium mr-2">{getProductName(item.product_id)}</span>
                      <span className="mx-2 text-neutral-500">x{item.quantity}</span>
                      <span className="text-green-700 font-semibold mr-2">{formatCurrency(itemPrice(item.price, item.product_id))}</span>
                      <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); openModal('accessoire', fullIdx); }} className="ml-1 text-blue-500 hover:text-blue-700"><Edit className="w-4 h-4" /></button>
                      <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeItem(fullIdx); }} className="ml-1 text-red-500 hover:text-red-700"><X className="w-4 h-4" /></button>
                    </div>
                  );
                })}
                {form.items.filter(i => i.item_type === 'accessoire').length === 0 && (
                  <span className="text-neutral-400">Aucun accessoire ajouté</span>
                )}
              </div>
            </ModernCard>
            <ModernCard className="mb-6 p-4 shadow-lg">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-primary-700">Lentilles</h3>
                <ModernButton variant="primary" size="sm" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setLensSelectionTarget('lentilles'); setLensSelectionModal(true); }}>
                  <div className="flex items-center">
                    <Plus className="w-4 h-4" />
                    <span className="ml-2">Ajouter</span>
                  </div>
                </ModernButton>
              </div>
              <div className="flex flex-wrap gap-2">
                {(() => {
                  const lentilles = form.items.filter(i => i.item_type === 'lentilles');
                  if (lentilles.length === 0) {
                    return <span className="text-neutral-400">Aucune lentille ajoutée</span>;
                  }
                  return lentilles.map((item) => {
                    const fullIdx = form.items.findIndex(it => it === item);
                    const sideLabel = item.side === 'left' ? 'Gauche' : item.side === 'right' ? 'Droit' : '';
                    const corr = formatOpticalCorrectionSummary(item);
                    return (
                      <div key={fullIdx} className="flex items-center bg-purple-50 rounded-full px-4 py-2 shadow-sm border border-purple-200">
                        <span className="font-medium mr-2">{getProductName(item.product_id)}</span>
                        {sideLabel && <span className="mx-1 text-neutral-500">{sideLabel}</span>}
                        {corr && <span className="mx-1 text-xs text-purple-600">{corr}</span>}
                        <span className="mx-2 text-neutral-500">x{item.quantity}</span>
                        <span className="text-purple-700 font-semibold mr-2">{formatCurrency(itemPrice(item.price, item.product_id))}</span>
                        <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); openModal('lentilles', fullIdx); }} className="ml-1 text-blue-500 hover:text-blue-700"><Edit className="w-4 h-4" /></button>
                        <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeItem(fullIdx); }} className="ml-1 text-red-500 hover:text-red-700"><X className="w-4 h-4" /></button>
                      </div>
                    );
                  });
                })()}
              </div>
            </ModernCard>
          </div>



          {/* Total display */}
          <div className="flex items-center justify-end mt-4">
            <span className="text-lg font-bold text-success-700">Total: {form.items.reduce((sum, item) => sum + itemPrice(item.price, item.product_id) * Number(item.quantity), 0).toLocaleString('fr-FR', { style: 'currency', currency: 'MAD' })}</span>
          </div>
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-neutral-200">
            <ModernButton
              type="button"
              variant="secondary"
              onClick={() => {
                setShowForm(false);
                setEditOrderLoading(false);
                setDeletedPreviewMode(false);
              }}
            >
              Annuler
            </ModernButton>
            <ModernButton
              type="submit"
              variant="primary"
              icon={<Save className="w-4 h-4" />}
              disabled={deletedPreviewMode || submitLoading || editOrderLoading}
            >
              {deletedPreviewMode ? 'Lecture seule' : (submitLoading ? 'Enregistrement...' : (editingId ? 'Modifier' : 'Ajouter'))}
            </ModernButton>
          </div>
          </fieldset>
        </form>
        </div>
      </ModernModal>

      {/* Lens Selection Modal */}
      <ModernModal isOpen={lensSelectionModal} onClose={() => setLensSelectionModal(false)} title={lensSelectionTarget === 'lentilles' ? 'Sélection du type de lentille' : 'Sélection du type de verre'} size="md">
        <div className="space-y-4">
          <div className="text-center mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {lensSelectionTarget === 'lentilles' ? 'Choisissez le type de lentille' : 'Choisissez le type de verre'}
            </h3>
            <p className="text-sm text-gray-600">
              {lensSelectionTarget === 'lentilles'
                ? 'Sélectionnez comment vous souhaitez ajouter les lentilles'
                : 'Sélectionnez comment vous souhaitez ajouter les verres'}
            </p>
          </div>
          
          <div className="grid grid-cols-1 gap-4">
            <button
              onClick={() => {
                setLensSelectionType('both_same');
                setLensSelectionModal(false);
                // Reset product selections for same product
                setLeftLensProduct({ product_id: '', price: '' });
                setRightLensProduct({ product_id: '', price: '' });
                openModal(lensSelectionTarget);
              }}
              className="p-4 border-2 border-blue-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors text-left"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-blue-700">
                    {lensSelectionTarget === 'lentilles' ? 'Deux yeux - Même lentille' : 'Deux yeux - Même verre'}
                  </h4>
                  <p className="text-sm text-gray-600">Un seul produit pour les deux yeux</p>
                </div>
                <div className="text-blue-500">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
              </div>
            </button>
            
            <button
              onClick={() => {
                setLensSelectionType('both_different');
                setLensSelectionModal(false);
                // Reset product selections for different products
                setLeftLensProduct({ product_id: '', price: '' });
                setRightLensProduct({ product_id: '', price: '' });
                openModal(lensSelectionTarget);
              }}
              className="p-4 border-2 border-green-200 rounded-lg hover:border-green-400 hover:bg-green-50 transition-colors text-left"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-green-700">
                    {lensSelectionTarget === 'lentilles' ? 'Deux yeux - Lentilles différentes' : 'Deux yeux - Verres différents'}
                  </h4>
                  <p className="text-sm text-gray-600">Deux produits différents (gauche et droit)</p>
                </div>
                <div className="text-green-500">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
              </div>
            </button>
            
            <button
              onClick={() => {
                setLensSelectionType('left_only');
                setLensSelectionModal(false);
                openModal(lensSelectionTarget);
              }}
              className="p-4 border-2 border-orange-200 rounded-lg hover:border-orange-400 hover:bg-orange-50 transition-colors text-left"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-orange-700">Œil gauche seulement</h4>
                  <p className="text-sm text-gray-600">
                    {lensSelectionTarget === 'lentilles'
                      ? "Uniquement la lentille pour l'œil gauche"
                      : "Uniquement le verre pour l'œil gauche"}
                  </p>
                </div>
                <div className="text-orange-500">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
              </div>
            </button>
            
            <button
              onClick={() => {
                setLensSelectionType('right_only');
                setLensSelectionModal(false);
                openModal(lensSelectionTarget);
              }}
              className="p-4 border-2 border-purple-200 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-colors text-left"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-purple-700">Œil droit seulement</h4>
                  <p className="text-sm text-gray-600">
                    {lensSelectionTarget === 'lentilles'
                      ? "Uniquement la lentille pour l'œil droit"
                      : "Uniquement le verre pour l'œil droit"}
                  </p>
                </div>
                <div className="text-purple-500">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
              </div>
            </button>
          </div>
        </div>
      </ModernModal>

      {/* Add/Edit Item Modal - moved outside the main form */}
      <ModernModal
        isOpen={modalOpen}
        onClose={closeModal}
        title={
          modalEditIdx !== null
            ? (modalType === 'lentilles' ? 'Modifier les lentilles' : modalType === 'verre' ? 'Modifier le verre' : `Modifier ${modalType}`)
            : (modalType === 'lentilles' ? 'Ajouter des lentilles' : modalType === 'verre' ? 'Ajouter un verre' : `Ajouter ${modalType}`)
        }
        size={opticalModalExpanded ? 'wide' : 'lg'}
        className={opticalModalExpanded ? 'optical-product-modal' : ''}
      >
        <div className={opticalModalExpanded ? 'optical-modal-layout' : 'space-y-4'}>
          <div className="space-y-3 min-w-0">
          {/* Search Bar */}
          <div className="relative">
            <input
              type="text"
              placeholder={`Rechercher ${modalType === 'monture' ? 'une monture' : modalType === 'verre' ? 'un verre' : modalType === 'lentilles' ? 'une lentille' : 'un accessoire'}...`}
              value={modalSearchTerm}
              onChange={(e) => setModalSearchTerm(e.target.value)}
              className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <div className="absolute right-3 top-3 text-neutral-400">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {/* Product List */}
          {modalSearchTerm.trim() ? (
            <div className={`${isOpticalItemType(modalType) ? 'max-h-44' : 'max-h-64'} overflow-y-auto border border-neutral-200 rounded-lg`}>
              {getFilteredProducts(modalType, modalSearchTerm).length === 0 ? (
                <div className="p-4 text-center text-neutral-500">
                  Aucun produit trouvé
                </div>
              ) : (
                <div className="divide-y divide-neutral-200">
                  {getFilteredProducts(modalType, modalSearchTerm).map((option) => (
                    <div
                      key={option.value}
                      onClick={() => {
                        if (isOpticalItemType(modalType) && lensSelectionType === 'both_different') {
                          // For different products, show selection for left or right
                          if (!leftLensProduct.product_id) {
                            setLeftLensProduct({ product_id: option.value, price: option.product.sale_price });
                          } else if (!rightLensProduct.product_id) {
                            setRightLensProduct({ product_id: option.value, price: option.product.sale_price });
                          }
                        } else {
                          // For same product or single eye, use normal selection
                          handleModalChange('product_id', option.value);
                        }
                      }}
                      className={`p-3 cursor-pointer hover:bg-neutral-50 transition-colors ${
                        (modalForm.product_id === option.value) || 
                        (isOpticalItemType(modalType) && lensSelectionType === 'both_different' && 
                         (leftLensProduct.product_id === option.value || rightLensProduct.product_id === option.value))
                          ? 'bg-primary-50 border-l-4 border-primary-500' : ''
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-medium text-neutral-900">{option.product.name}</div>
                          <div className="text-sm text-neutral-600">
                            {option.product.brand && `${option.product.brand}`}
                            {option.product.model && ` - ${option.product.model}`}
                            {option.product.category_name && ` (${option.product.category_name})`}
                          </div>
                          <div className="text-sm text-success-600 font-semibold">
                            {formatCurrency(option.product.sale_price)}
                          </div>
                          {isOpticalItemType(modalType) && lensSelectionType === 'both_different' && (
                            <div className="text-xs text-blue-600 mt-1">
                              {leftLensProduct.product_id === option.value ? `← ${modalType === 'lentilles' ? 'Lentille Gauche' : 'Verre Gauche'}` : 
                               rightLensProduct.product_id === option.value ? `→ ${modalType === 'lentilles' ? 'Lentille Droit' : 'Verre Droit'}` : ''}
                            </div>
                          )}
                        </div>
                        {(modalForm.product_id === option.value) || 
                         (isOpticalItemType(modalType) && lensSelectionType === 'both_different' && 
                          (leftLensProduct.product_id === option.value || rightLensProduct.product_id === option.value)) ? (
                          <div className="text-primary-600">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="p-8 text-center text-neutral-500 border border-neutral-200 rounded-lg bg-neutral-50">
              <svg className="w-12 h-12 mx-auto mb-3 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <p className="text-lg font-medium mb-2">Recherchez un produit</p>
              <p className="text-sm">Commencez à taper pour rechercher parmi vos produits</p>
            </div>
          )}

          </div>

          <div className="space-y-3 min-w-0">

          {/* Selected Product Info */}
          {isOpticalItemType(modalType) && lensSelectionType === 'both_different' ? (
            <div className="space-y-3">
              {/* Left Lens Product */}
              {leftLensProduct.product_id && (
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <div className="text-blue-700 font-semibold">
                    {modalType === 'lentilles' ? 'Lentille Gauche' : 'Verre Gauche'}: {getProductName(leftLensProduct.product_id)}
                    {formatOpticalCorrectionSummary(leftLensForm) && (
                      <span className="ml-2 font-normal text-blue-600">{formatOpticalCorrectionSummary(leftLensForm)}</span>
                    )}
                  </div>
                  <div className="text-sm text-blue-600">
                    Prix: {formatCurrency(getProductSalePrice(leftLensProduct.product_id))}
                  </div>
                </div>
              )}
              
              {/* Right Lens Product */}
              {rightLensProduct.product_id && (
                <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                  <div className="text-green-700 font-semibold">
                    {modalType === 'lentilles' ? 'Lentille Droit' : 'Verre Droit'}: {getProductName(rightLensProduct.product_id)}
                    {formatOpticalCorrectionSummary(rightLensForm) && (
                      <span className="ml-2 font-normal text-green-600">{formatOpticalCorrectionSummary(rightLensForm)}</span>
                    )}
                  </div>
                  <div className="text-sm text-green-600">
                    Prix: {formatCurrency(getProductSalePrice(rightLensProduct.product_id))}
                  </div>
                </div>
              )}
              
              {/* Selection Status */}
              {(!leftLensProduct.product_id || !rightLensProduct.product_id) && (
                <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                  <div className="text-yellow-700 text-sm">
                    {!leftLensProduct.product_id && !rightLensProduct.product_id ? 
                      (modalType === 'lentilles' ? "Sélectionnez d'abord la lentille gauche, puis la lentille droite" : "Sélectionnez d'abord le verre gauche, puis le verre droit") :
                      !leftLensProduct.product_id ? 
                      (modalType === 'lentilles' ? 'Sélectionnez la lentille gauche' : 'Sélectionnez le verre gauche') :
                      (modalType === 'lentilles' ? 'Sélectionnez la lentille droite' : 'Sélectionnez le verre droit')
                    }
                  </div>
                </div>
              )}
            </div>
          ) : modalForm.product_id && (
            <div className="bg-primary-50 p-3 rounded-lg">
              <div className="text-primary-700 font-semibold">
                Produit sélectionné: {getProductName(modalForm.product_id)}
              </div>
              <div className="text-sm text-primary-600">
                Prix: {formatCurrency(getProductSalePrice(modalForm.product_id))}
              </div>
              {isOpticalItemType(modalType) && (
                <div className="mt-2 space-y-1">
                  <div className="text-xs text-primary-600">
                    <span className="font-medium">{modalType === 'lentilles' ? 'Lentille Gauche:' : 'Verre Gauche:'}</span>{' '}
                    {formatOpticalCorrectionSummary(leftLensForm) || '—'}
                  </div>
                  <div className="text-xs text-primary-600">
                    <span className="font-medium">{modalType === 'lentilles' ? 'Lentille Droit:' : 'Verre Droit:'}</span>{' '}
                    {formatOpticalCorrectionSummary(rightLensForm) || '—'}
                  </div>
                </div>
              )}
            </div>
          )}

          {isOpticalItemType(modalType) && (modalForm.product_id || (lensSelectionType === 'both_different' && leftLensProduct.product_id && rightLensProduct.product_id)) && (
            <div className="optical-modal-compact-panel border-purple-200 bg-purple-50">
              <h4 className="font-semibold text-purple-700 mb-2 text-sm">Remarque</h4>
              <textarea
                value={lensRemarque}
                onChange={(e) => setLensRemarque(e.target.value)}
                placeholder={modalType === 'lentilles' ? 'Remarque lentille...' : 'Remarque verre...'}
                rows="2"
                className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
              />
            </div>
          )}

          {/* Optical parameters for glasses */}
          {isOpticalItemType(modalType) && (modalForm.product_id || (lensSelectionType === 'both_different' && (leftLensProduct.product_id || rightLensProduct.product_id))) && (
            <div className="optical-eyes-grid">
              {(lensSelectionType === 'both_same' || lensSelectionType === 'both_different' || lensSelectionType === 'right_only' || lensSelectionType === '') && (
                <div className="optical-modal-compact-panel border-green-200 bg-green-50">
                  <h4 className="font-semibold text-green-700 mb-2 text-sm">Oeil Droit</h4>
                  <div className="optical-eye-fields">
                    <ModernOpticalSelect 
                      label="Sph" 
                      value={rightLensForm.sph ?? ''} 
                      onChange={e => setRightLensForm(prev => ({ ...prev, sph: e.target.value }))} 
                      placeholder="Sélectionner Sph" 
                    />
                    <ModernOpticalSelect 
                      label="Cyl" 
                      value={rightLensForm.cyl ?? ''} 
                      onChange={e => setRightLensForm(prev => ({ ...prev, cyl: e.target.value }))} 
                      placeholder="Sélectionner Cyl" 
                    />
                    <ModernInput 
                      label="Axe" 
                      type="number" 
                      step="0.01"
                      value={rightLensForm.axe ?? ''} 
                      onChange={e => setRightLensForm(prev => ({ ...prev, axe: e.target.value }))} 
                      placeholder="Axe"
                      className="no-spinner"
                    />
                    <ModernInput 
                      label="Addition" 
                      type="number" 
                      step="0.01" 
                      value={rightLensForm.addition ?? ''} 
                      onChange={e => setRightLensForm(prev => ({ ...prev, addition: e.target.value }))} 
                      placeholder="Addition"
                      className="no-spinner"
                    />
                  </div>
                </div>
              )}

              {/* Left Eye - Show for both_same, both_different, and left_only */}
              {(lensSelectionType === 'both_same' || lensSelectionType === 'both_different' || lensSelectionType === 'left_only' || lensSelectionType === '') && (
                <div className="optical-modal-compact-panel border-blue-200 bg-blue-50">
                  <h4 className="font-semibold text-blue-700 mb-2 text-sm">Oeil Gauche</h4>
                  <div className="optical-eye-fields">
                    <ModernOpticalSelect 
                      label="Sph" 
                      value={leftLensForm.sph ?? ''} 
                      onChange={e => setLeftLensForm(prev => ({ ...prev, sph: e.target.value }))} 
                      placeholder="Sélectionner Sph" 
                    />
                    <ModernOpticalSelect 
                      label="Cyl" 
                      value={leftLensForm.cyl ?? ''} 
                      onChange={e => setLeftLensForm(prev => ({ ...prev, cyl: e.target.value }))} 
                      placeholder="Sélectionner Cyl" 
                    />
                    <ModernInput 
                      label="Axe" 
                      type="number" 
                      step="0.01"
                      value={leftLensForm.axe ?? ''} 
                      onChange={e => setLeftLensForm(prev => ({ ...prev, axe: e.target.value }))} 
                      placeholder="Axe"
                      className="no-spinner"
                    />
                    <ModernInput 
                      label="Addition" 
                      type="number" 
                      step="0.01" 
                      value={leftLensForm.addition ?? ''} 
                      onChange={e => setLeftLensForm(prev => ({ ...prev, addition: e.target.value }))} 
                      placeholder="Addition"
                      className="no-spinner"
                    />
                  </div>
                </div>
              )}

            </div>
          )}

          {/* PD and Height Measurements — verres only */}
          {modalType === 'verre' && (modalForm.product_id || (lensSelectionType === 'both_different' && (leftLensProduct.product_id || rightLensProduct.product_id))) && (
            <div className="space-y-3">
              <div className="optical-modal-compact-panel border-gray-200 bg-gray-50">
                <h4 className="font-semibold text-gray-700 mb-2 text-sm">Mesures Pupillaires</h4>
                    
                    {(lensSelectionType === 'both_same' || lensSelectionType === '') && (
                      <div className="optical-pd-pair">
                        <div className="optical-modal-compact-panel border-blue-200 bg-blue-50">
                          <h5 className="font-semibold text-blue-700 mb-2 text-sm text-center">Vision de loin</h5>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-sm font-medium text-blue-600 mb-1">ÉCART DROIT (verre droit)</label>
                              <input
                                type="number"
                                step="0.01"
                                value={pdMeasurements.pd_right_verre_droit ?? ''}
                                onChange={(e) => setPdMeasurements(prev => ({ ...prev, pd_right_verre_droit: e.target.value }))}
                                placeholder="0.00"
                                className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 no-spinner"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-blue-600 mb-1">ÉCART GAUCHE (verre droit)</label>
                              <input
                                type="number"
                                step="0.01"
                                value={pdMeasurements.pd_left_verre_droit ?? ''}
                                onChange={(e) => setPdMeasurements(prev => ({ ...prev, pd_left_verre_droit: e.target.value }))}
                                placeholder="0.00"
                                className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 no-spinner"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Vision de près section */}
                        <div className="optical-modal-compact-panel border-orange-200 bg-orange-50">
                          <h5 className="font-semibold text-orange-700 mb-2 text-sm text-center">Vision de près</h5>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-sm font-medium text-orange-600 mb-1">ÉCART DROIT (verre gauche)</label>
                              <input
                                type="number"
                                step="0.01"
                                value={pdMeasurements.pd_right_verre_gauche ?? ''}
                                onChange={(e) => setPdMeasurements(prev => ({ ...prev, pd_right_verre_gauche: e.target.value }))}
                                placeholder="0.00"
                                className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 no-spinner"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-orange-600 mb-1">ÉCART GAUCHE (verre gauche)</label>
                              <input
                                type="number"
                                step="0.01"
                                value={pdMeasurements.pd_left_verre_gauche ?? ''}
                                onChange={(e) => setPdMeasurements(prev => ({ ...prev, pd_left_verre_gauche: e.target.value }))}
                                placeholder="0.00"
                                className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 no-spinner"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* For different lenses, show separate sections */}
                    {lensSelectionType === 'both_different' && (
                      <div className="optical-pd-pair">
                        <div className="optical-modal-compact-panel border-blue-200 bg-blue-50">
                          <h5 className="font-semibold text-blue-700 mb-2 text-sm text-center">Vision de loin</h5>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-sm font-medium text-blue-600 mb-1">ÉCART DROIT (verre droit)</label>
                              <input
                                type="number"
                                step="0.01"
                                value={pdMeasurements.pd_right_verre_droit ?? ''}
                                onChange={(e) => setPdMeasurements(prev => ({ ...prev, pd_right_verre_droit: e.target.value }))}
                                placeholder="0.00"
                                className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 no-spinner"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-blue-600 mb-1">ÉCART GAUCHE (verre droit)</label>
                              <input
                                type="number"
                                step="0.01"
                                value={pdMeasurements.pd_left_verre_droit ?? ''}
                                onChange={(e) => setPdMeasurements(prev => ({ ...prev, pd_left_verre_droit: e.target.value }))}
                                placeholder="0.00"
                                className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 no-spinner"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Vision de près section */}
                        <div className="optical-modal-compact-panel border-orange-200 bg-orange-50">
                          <h5 className="font-semibold text-orange-700 mb-2 text-sm text-center">Vision de près</h5>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-sm font-medium text-orange-600 mb-1">ÉCART DROIT (verre gauche)</label>
                              <input
                                type="number"
                                step="0.01"
                                value={pdMeasurements.pd_right_verre_gauche ?? ''}
                                onChange={(e) => setPdMeasurements(prev => ({ ...prev, pd_right_verre_gauche: e.target.value }))}
                                placeholder="0.00"
                                className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 no-spinner"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-orange-600 mb-1">ÉCART GAUCHE (verre gauche)</label>
                              <input
                                type="number"
                                step="0.01"
                                value={pdMeasurements.pd_left_verre_gauche ?? ''}
                                onChange={(e) => setPdMeasurements(prev => ({ ...prev, pd_left_verre_gauche: e.target.value }))}
                                placeholder="0.00"
                                className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 no-spinner"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* For single lens (left only) - Vision de près only */}
                    {lensSelectionType === 'left_only' && (
                      <div className="border border-orange-200 rounded-lg p-4 bg-orange-50">
                        <h5 className="font-semibold text-orange-700 mb-3 text-center">Vision de près - Verre Gauche Seulement</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-orange-600 mb-1">ÉCART DROIT (verre gauche)</label>
                            <input
                              type="number"
                              step="0.01"
                              value={pdMeasurements.pd_right_verre_gauche ?? ''}
                              onChange={(e) => setPdMeasurements(prev => ({ ...prev, pd_right_verre_gauche: e.target.value }))}
                              placeholder="0.00"
                              className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-orange-600 mb-1">ÉCART GAUCHE (verre gauche)</label>
                            <input
                              type="number"
                              step="0.01"
                              value={pdMeasurements.pd_left_verre_gauche ?? ''}
                              onChange={(e) => setPdMeasurements(prev => ({ ...prev, pd_left_verre_gauche: e.target.value }))}
                              placeholder="0.00"
                              className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* For single lens (right only) - Vision de loin only */}
                    {lensSelectionType === 'right_only' && (
                      <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                        <h5 className="font-semibold text-blue-700 mb-3 text-center">Vision de loin - Verre Droit Seulement</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-blue-600 mb-1">ÉCART DROIT (verre droit)</label>
                            <input
                              type="number"
                              step="0.01"
                              value={pdMeasurements.pd_right_verre_droit ?? ''}
                              onChange={(e) => setPdMeasurements(prev => ({ ...prev, pd_right_verre_droit: e.target.value }))}
                              placeholder="0.00"
                              className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-blue-600 mb-1">ÉCART GAUCHE (verre droit)</label>
                            <input
                              type="number"
                              step="0.01"
                              value={pdMeasurements.pd_left_verre_droit ?? ''}
                              onChange={(e) => setPdMeasurements(prev => ({ ...prev, pd_left_verre_droit: e.target.value }))}
                              placeholder="0.00"
                              className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Height Measurements */}
                  <div className="border border-orange-200 rounded-lg p-4 bg-orange-50">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-orange-700">Mesures de Hauteur</h4>
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="showAdditionalHauteursCommandes"
                          checked={showAdditionalHauteurs}
                          onChange={(e) => setShowAdditionalHauteurs(e.target.checked)}
                          className="rounded border-orange-300 text-orange-600 focus:ring-orange-500"
                        />
                        <label htmlFor="showAdditionalHauteursCommandes" className="text-sm font-medium text-orange-600">
                          Hauteurs supplémentaires (3 & 4)
                        </label>
                      </div>
                    </div>
                    
                    <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${showAdditionalHauteurs ? 'mb-4' : ''}`}>
                      <div>
                        <label className="block text-sm font-medium text-orange-600 mb-1">HAUTEUR 1</label>
                        <input
                          type="number"
                          step="0.01"
                          value={pdMeasurements.hauteur1 ?? ''}
                          onChange={(e) => setPdMeasurements(prev => ({ ...prev, hauteur1: e.target.value }))}
                          placeholder="0.00"
                          className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 no-spinner"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-orange-600 mb-1">HAUTEUR 2</label>
                        <input
                          type="number"
                          step="0.01"
                          value={pdMeasurements.hauteur2 ?? ''}
                          onChange={(e) => setPdMeasurements(prev => ({ ...prev, hauteur2: e.target.value }))}
                          placeholder="0.00"
                          className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 no-spinner"
                        />
                      </div>
                    </div>
                    
                    {showAdditionalHauteurs && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-orange-600 mb-1">HAUTEUR 3</label>
                          <input
                            type="number"
                            step="0.01"
                            value={pdMeasurements.hauteur3 ?? ''}
                            onChange={(e) => setPdMeasurements(prev => ({ ...prev, hauteur3: e.target.value }))}
                            placeholder="0.00"
                            className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 no-spinner"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-orange-600 mb-1">HAUTEUR 4</label>
                          <input
                            type="number"
                            step="0.01"
                            value={pdMeasurements.hauteur4 ?? ''}
                            onChange={(e) => setPdMeasurements(prev => ({ ...prev, hauteur4: e.target.value }))}
                            placeholder="0.00"
                            className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 no-spinner"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

          {/* Price and Quantity */}
          {modalForm.product_id && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Prix (MAD)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={priceInputValue(modalForm.price)}
                  onChange={(e) => handleModalChange('price', e.target.value)}
                  placeholder={`${getProductSalePrice(modalForm.product_id)}`}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Quantité</label>
                <input
                  type="number"
                  min="1"
                  value={modalForm.quantity}
                  onChange={(e) => handleModalChange('quantity', e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          )}

          {/* Total */}
          {modalForm.product_id && (
            <div className="text-lg font-semibold text-neutral-900">
              Total: {formatCurrency(itemPrice(modalForm.price, modalForm.product_id) * (modalForm.quantity || 1))}
            </div>
          )}

          {modalError && <div className="text-red-500 text-sm">{modalError}</div>}

          {!isOpticalItemType(modalType) && (
          <div className="text-xs text-neutral-500 bg-neutral-50 p-3 rounded-lg">
            💡 <strong>Note:</strong> Le produit sera ajouté à votre panier. Cliquez sur "Ajouter" pour finaliser l'ajout.
          </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-2">
            <ModernButton type="button" variant="secondary" onClick={closeModal}>Annuler</ModernButton>
            <ModernButton type="button" variant="primary" onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!modalForm.product_id && !isOpticalItemType(modalType)) {
                setModalError('Veuillez sélectionner un produit.');
                return;
              }
              if (isOpticalItemType(modalType) && lensSelectionType === 'both_different' && (!leftLensProduct.product_id || !rightLensProduct.product_id)) {
                setModalError('Veuillez sélectionner les deux produits (gauche et droit).');
                return;
              }
              if (!modalForm.quantity || modalForm.quantity < 1) {
                setModalError('Veuillez entrer une quantité valide.');
                return;
              }
              setModalError('');
              const price = itemPrice(modalForm.price, modalForm.product_id);
              
              if (modalType === 'verre' || modalType === 'lentilles') {
                const itemType = modalType;
                const rem = lensRemarque;
                const pickOptical = (formVal, itemVal) =>
                  hasOpticalValue(formVal) ? formVal : (hasOpticalValue(itemVal) ? itemVal : '');
                const updatedMsg = itemType === 'lentilles' ? 'Lentilles modifiées avec succès' : 'Verres modifiés avec succès';
                const updatedOneMsg = itemType === 'lentilles' ? 'Lentille modifiée avec succès' : 'Verre modifié avec succès';
                const addedMsg = itemType === 'lentilles' ? 'Lentilles ajoutées au panier' : 'Verres ajoutés au panier';
                const addedOneMsg = itemType === 'lentilles' ? 'Lentille ajoutée au panier' : 'Verre ajouté au panier';

                if (modalEditIdx !== null) {
                  if (lensSelectionType === 'both_different') {
                    const leftIdx = form.items.findIndex(
                      (item) => item.item_type === itemType && item.side === 'left'
                    );
                    const rightIdx = form.items.findIndex(
                      (item) => item.item_type === itemType && item.side === 'right'
                    );
                    const leftPrice = itemPrice(leftLensProduct.price, leftLensProduct.product_id);
                    const rightPrice = itemPrice(rightLensProduct.price, rightLensProduct.product_id);

                    if (leftIdx !== -1) {
                      updateItem(leftIdx, {
                        ...form.items[leftIdx],
                        item_type: itemType,
                        product_id: leftLensProduct.product_id || form.items[leftIdx].product_id,
                        price: leftPrice,
                        side: 'left',
                        ...leftLensForm,
                        remarque: rem,
                      });
                    }
                    if (rightIdx !== -1) {
                      updateItem(rightIdx, {
                        ...form.items[rightIdx],
                        item_type: itemType,
                        product_id: rightLensProduct.product_id || form.items[rightIdx].product_id,
                        price: rightPrice,
                        side: 'right',
                        ...rightLensForm,
                        remarque: rem,
                      });
                    }
                    toast.success(updatedMsg);
                  } else if (lensSelectionType === 'left_only') {
                    updateItem(modalEditIdx, {
                      ...modalForm,
                      item_type: itemType,
                      price,
                      side: 'left',
                      sph: pickOptical(leftLensForm.sph, modalForm.sph),
                      cyl: pickOptical(leftLensForm.cyl, modalForm.cyl),
                      axe: pickOptical(leftLensForm.axe, modalForm.axe),
                      addition: pickOptical(leftLensForm.addition, modalForm.addition),
                      remarque: rem,
                    });
                    toast.success(updatedOneMsg);
                  } else if (lensSelectionType === 'right_only') {
                    updateItem(modalEditIdx, {
                      ...modalForm,
                      item_type: itemType,
                      price,
                      side: 'right',
                      sph: pickOptical(rightLensForm.sph, modalForm.sph),
                      cyl: pickOptical(rightLensForm.cyl, modalForm.cyl),
                      axe: pickOptical(rightLensForm.axe, modalForm.axe),
                      addition: pickOptical(rightLensForm.addition, modalForm.addition),
                      remarque: rem,
                    });
                    toast.success(updatedOneMsg);
                  } else {
                    const leftLensItem = {
                      ...modalForm,
                      item_type: itemType,
                      side: 'left',
                      price,
                      ...leftLensForm,
                      remarque: rem
                    };
                    const rightLensItem = {
                      ...modalForm,
                      item_type: itemType,
                      side: 'right',
                      price,
                      ...rightLensForm,
                      remarque: rem
                    };
                    const editingLegacy = form.items[modalEditIdx]?.side === 'none';
                    if (editingLegacy) {
                      setForm(f => ({
                        ...f,
                        items: [
                          ...f.items.filter((_, i) => i !== modalEditIdx),
                          leftLensItem,
                          rightLensItem,
                        ],
                      }));
                    } else {
                    const leftItemIndex = form.items.findIndex(item =>
                      item.product_id === modalForm.product_id &&
                      item.item_type === itemType &&
                      item.side === 'left'
                    );
                    const rightItemIndex = form.items.findIndex(item =>
                      item.product_id === modalForm.product_id &&
                      item.item_type === itemType &&
                      item.side === 'right'
                    );
                    if (leftItemIndex !== -1) updateItem(leftItemIndex, leftLensItem);
                    if (rightItemIndex !== -1) updateItem(rightItemIndex, rightLensItem);
                    }
                    toast.success(updatedMsg);
                  }
                } else if (lensSelectionType === 'both_different') {
                  if (!leftLensProduct.product_id || !rightLensProduct.product_id) {
                    setModalError('Veuillez sélectionner les deux produits (gauche et droit)');
                    return;
                  }
                  const leftPrice = itemPrice(leftLensProduct.price, leftLensProduct.product_id);
                  const rightPrice = itemPrice(rightLensProduct.price, rightLensProduct.product_id);
                  addItem({
                    ...modalForm,
                    item_type: itemType,
                    product_id: leftLensProduct.product_id,
                    price: leftPrice,
                    side: 'left',
                    sph: leftLensForm.sph,
                    cyl: leftLensForm.cyl,
                    axe: leftLensForm.axe,
                    addition: leftLensForm.addition,
                    remarque: rem
                  });
                  addItem({
                    ...modalForm,
                    item_type: itemType,
                    product_id: rightLensProduct.product_id,
                    price: rightPrice,
                    side: 'right',
                    sph: rightLensForm.sph,
                    cyl: rightLensForm.cyl,
                    axe: rightLensForm.axe,
                    addition: rightLensForm.addition,
                    remarque: rem
                  });
                  toast.success(addedMsg);
                } else if (lensSelectionType === 'both_same' || lensSelectionType === '') {
                  const leftLensItem = {
                    ...modalForm,
                    item_type: itemType,
                    price,
                    side: 'left',
                    ...leftLensForm,
                    remarque: rem
                  };
                  const rightLensItem = {
                    ...modalForm,
                    item_type: itemType,
                    price,
                    side: 'right',
                    ...rightLensForm,
                    remarque: rem
                  };
                  addItem(leftLensItem);
                  addItem(rightLensItem);
                  toast.success(addedMsg);
                } else if (lensSelectionType === 'left_only') {
                  addItem({
                    ...modalForm,
                    item_type: itemType,
                    price,
                    side: 'left',
                    sph: leftLensForm.sph,
                    cyl: leftLensForm.cyl,
                    axe: leftLensForm.axe,
                    addition: leftLensForm.addition,
                    remarque: rem
                  });
                  toast.success(addedOneMsg);
                } else if (lensSelectionType === 'right_only') {
                  addItem({
                    ...modalForm,
                    item_type: itemType,
                    price,
                    side: 'right',
                    sph: rightLensForm.sph,
                    cyl: rightLensForm.cyl,
                    axe: rightLensForm.axe,
                    addition: rightLensForm.addition,
                    remarque: rem
                  });
                  toast.success(addedOneMsg);
                } else {
                  setModalError('Type de produit non défini');
                  return;
                }
              } else {
                // Handle non-lens items normally
                const itemToSave = { ...modalForm, price };
                
                if (modalEditIdx !== null) {
                  updateItem(modalEditIdx, itemToSave);
                  toast.success('Produit modifié avec succès');
                } else {
                  // Allow multiple same products - no duplicate check
                  addItem(itemToSave);
                  toast.success('Produit ajouté au panier');
                }
              }
              closeModal();
            }}>{modalEditIdx !== null ? 'Enregistrer' : 'Ajouter'}</ModernButton>
          </div>
          </div>
        </div>
      </ModernModal>

      {showPaymentModal && selectedOrder && (
        <ModernModal isOpen={showPaymentModal} onClose={() => setShowPaymentModal(false)} title={`Paiements pour commande #${selectedOrder.id}`} size="full" className="max-w-6xl w-full">
          <div className="space-y-6">
            {/* Totals Row */}
            <div className="flex flex-wrap items-center gap-6 border-b pb-4">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-neutral-700">Total payé:</span>
                <span className="text-lg font-bold text-success-700 flex items-center gap-1">
                  {formatCurrency((Array.isArray(orderPayments) ? orderPayments : []).reduce((sum, p) => sum + Number(p.amount), 0))}
                  {getPaymentStatus(selectedOrder, Array.isArray(orderPayments) ? orderPayments : []).color === 'success' && <CheckCircle className="w-5 h-5 text-success-500" />}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-neutral-700">Reste à payer:</span>
                <span className="text-lg font-bold text-warning-700 flex items-center gap-1">
                  {formatCurrency(Math.max(0, (selectedOrder.total || getOrderTotal(selectedOrder)) - (Array.isArray(orderPayments) ? orderPayments : []).reduce((sum, p) => sum + Number(p.amount), 0)))}
                  {getPaymentStatus(selectedOrder, Array.isArray(orderPayments) ? orderPayments : []).color === 'warning' && <AlertCircle className="w-5 h-5 text-warning-500" />}
                </span>
              </div>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-${getPaymentStatus(selectedOrder, Array.isArray(orderPayments) ? orderPayments : []).color}-100 text-${getPaymentStatus(selectedOrder, Array.isArray(orderPayments) ? orderPayments : []).color}-700 ml-2`}> 
                {(() => {
                  const paid = (Array.isArray(orderPayments) ? orderPayments : []).reduce((sum, p) => sum + Number(p.amount), 0);
                  const total = selectedOrder.total || getOrderTotal(selectedOrder);
                  if (paid === 0) return 'En attente';
                  if (paid < total) return 'Partiel';
                  return 'Payé';
                })()}
                {getPaymentStatus(selectedOrder, Array.isArray(orderPayments) ? orderPayments : []).color === 'danger' && <XCircle className="w-4 h-4 ml-1" />}
              </span>
            </div>
            {/* Payment History */}
            <div>
              <h4 className="font-semibold mb-2 flex items-center gap-2 text-primary-700"><Info className="w-4 h-4 text-primary-500" /> Historique des paiements</h4>
              {paymentLoading ? <ModernLoadingSpinner /> : (
                <div className="overflow-x-auto rounded-xl border bg-white">
                  <table className="min-w-full text-base">
                    <colgroup>
                      <col style={{ width: '22%' }} />
                      <col style={{ width: '18%' }} />
                      <col style={{ width: '18%' }} />
                      <col style={{ width: '22%' }} />
                      <col style={{ width: '20%' }} />
                    </colgroup>
                    <thead className="bg-neutral-50">
                      <tr>
                        <th className="text-left font-semibold px-5 py-3">Date paiement</th>
                        <th className="text-left font-semibold px-5 py-3">Montant</th>
                        <th className="text-left font-semibold px-5 py-3">Mode</th>
                        <th className="text-left font-semibold px-5 py-3">Note/Chèque</th>
                        <th className="text-left font-semibold px-5 py-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(Array.isArray(orderPayments) ? orderPayments : []).map((p, idx) => (
                        editingPaymentId === p.id ? (
                          <tr key={p.id} className="bg-yellow-50">
                            <td className="px-5 py-3">
                              <ModernInput
                                name="payment_date"
                                type="date"
                                value={editPayment.payment_date || ''}
                                onChange={e => setEditPayment(ep => ({ ...ep, payment_date: e.target.value }))}
                                required
                              />
                            </td>
                            <td className="px-5 py-3">
                              <ModernInput
                                name="amount"
                                type="number"
                                min="0.01"
                                step="0.01"
                                value={editPayment.amount}
                                onChange={e => setEditPayment(ep => ({ ...ep, amount: e.target.value }))}
                                required
                              />
                            </td>
                            <td className="px-5 py-3">
                              <ModernSelect
                                name="mode"
                                value={paymentModes.find(opt => opt.value === editPayment.mode) || null}
                                onChange={option =>
                                  setEditPayment((ep) => {
                                    const selectedMode = option ? option.value : '';
                                    if (selectedMode !== 'cheque') {
                                      return { ...ep, mode: selectedMode, cheque_number: '' };
                                    }
                                    return { ...ep, mode: selectedMode };
                                  })
                                }
                                options={paymentModes}
                              />
                            </td>
                            <td className="px-5 py-3">
                              {editPayment.mode === 'cheque' ? (
                                <ModernInput
                                  name="cheque_number"
                                  value={editPayment.cheque_number || ''}
                                  onChange={e => setEditPayment(ep => ({ ...ep, cheque_number: e.target.value }))}
                                  placeholder="Numéro de chèque..."
                                  required
                                />
                              ) : (
                                <ModernInput
                                  name="note"
                                  value={editPayment.note}
                                  onChange={e => setEditPayment(ep => ({ ...ep, note: e.target.value }))}
                                />
                              )}
                            </td>
                            <td className="flex gap-2 px-5 py-3">
                              <ModernButton type="button" variant="success" size="sm" onClick={handleSaveEditPayment} icon={<CheckCircle className="w-4 h-4" />}>Enregistrer</ModernButton>
                              <ModernButton type="button" variant="secondary" size="sm" onClick={cancelEditPayment} icon={<XCircle className="w-4 h-4" />}>Annuler</ModernButton>
                            </td>
                          </tr>
                        ) : (
                          <tr key={p.id} className={idx % 2 === 0 ? 'bg-neutral-50 hover:bg-neutral-100' : 'bg-white hover:bg-neutral-100'}>
                            <td className="px-5 py-3 whitespace-nowrap align-middle">
                              <span>{formatDateOnlyFr(p.payment_date)}</span>
                            </td>
                            <td className="px-5 py-3 font-semibold text-primary-700 whitespace-nowrap align-middle">{formatCurrency(p.amount)}</td>
                            <td className="px-5 py-3 align-middle">
                              <div className="flex items-center gap-1 whitespace-nowrap">
                                {p.mode === 'espece' && <Banknote className="w-4 h-4 text-success-500" />} 
                                {p.mode === 'cheque' && <Banknote className="w-4 h-4 text-info-500" />} 
                                {p.mode === 'carte_bancaire' && <CreditCard className="w-4 h-4 text-primary-500" />} 
                                {p.mode === 'virement' && <Banknote className="w-4 h-4 text-indigo-500" />} 
                                <span>{paymentModes.find(m => m.value === p.mode)?.label || p.mode}</span>
                              </div>
                            </td>
                            <td className="px-5 py-3 whitespace-pre-line align-middle">
                              {p.mode === 'cheque' && p.cheque_number ? (
                                <div className="flex flex-col gap-1">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-blue-600">Chèque #{p.cheque_number}</span>
                                    {p.note && <span className="text-gray-500">- {p.note}</span>}
                                  </div>
                                </div>
                              ) : (
                                p.note
                              )}
                            </td>
                            <td className="flex gap-2 px-5 py-3 align-middle items-center">
                              <ModernButton type="button" variant="ghost" size="sm" onClick={() => startEditPayment(p)} icon={<Edit className="w-4 h-4" />}>Modifier</ModernButton>
                              <ModernButton type="button" variant="danger" size="sm" onClick={() => handleDeletePayment(p.id)} icon={<Trash2 className="w-4 h-4" />}>Supprimer</ModernButton>
                            </td>
                          </tr>
                        )
                      ))}
                      {(Array.isArray(orderPayments) ? orderPayments : []).length === 0 && (
                        <tr>
                           <td colSpan={5} className="h-48 align-middle">
                             <div className="flex flex-col items-center justify-center h-full w-full">
                               <Info className="w-8 h-8 mb-2 text-primary-300" />
                               <span className="text-neutral-400 text-base">Aucun paiement enregistré pour cette commande.</span>
                             </div>
                           </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            {/* Add Payment Form */}
            <form onSubmit={handleAddPayment} className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end bg-neutral-50 p-4 rounded-xl border-t">
              <ModernInput
                label="Montant (MAD)"
                name="amount"
                type="number"
                min="0.01"
                step="0.01"
                value={newPayment.amount}
                onChange={e => setNewPayment(p => ({ ...p, amount: e.target.value }))}
                required
                icon={<Banknote className="w-4 h-4 text-primary-400" />}
              />
              <ModernSelect
                label="Mode"
                name="mode"
                value={paymentModes.find(opt => opt.value === newPayment.mode) || paymentModes[0]}
                onChange={option =>
                  setNewPayment((p) => {
                    const selectedMode = option ? option.value : '';
                    if (selectedMode !== 'cheque') {
                      return { ...p, mode: selectedMode, cheque_number: '' };
                    }
                    return { ...p, mode: selectedMode };
                  })
                }
                options={paymentModes}
                icon={<CreditCard className="w-4 h-4 text-primary-400" />}
              />
              <ModernInput
                label="Date du paiement"
                name="payment_date"
                type="date"
                value={newPayment.payment_date || ''}
                onChange={e => setNewPayment(p => ({ ...p, payment_date: e.target.value }))}
                required
                icon={<Calendar className="w-4 h-4 text-primary-400" />}
              />
              {newPayment.mode === 'cheque' ? (
                <ModernInput
                  label="Numéro de chèque"
                  name="cheque_number"
                  value={newPayment.cheque_number || ''}
                  onChange={e => setNewPayment(p => ({ ...p, cheque_number: e.target.value }))}
                  placeholder="Entrez le numéro de chèque..."
                  required
                  icon={<Info className="w-4 h-4 text-primary-400" />}
                />
              ) : (
                <ModernInput
                  label="Note"
                  name="note"
                  value={newPayment.note}
                  onChange={e => setNewPayment(p => ({ ...p, note: e.target.value }))}
                  icon={<Info className="w-4 h-4 text-primary-400" />}
                />
              )}
              <ModernButton type="submit" variant="primary" disabled={paymentLoading} icon={<Save className="w-4 h-4" />} className="w-full md:w-auto md:col-span-2">
                {paymentLoading ? <ModernLoadingSpinner size="sm" /> : 'Ajouter paiement'}
              </ModernButton>
            </form>
          </div>
        </ModernModal>
      )}

      {/* Payment Reminder Modal */}
      <ModernModal 
        isOpen={showPaymentReminderModal} 
        onClose={() => setShowPaymentReminderModal(false)} 
        title="Paiement manquant" 
        size="md"
      >
        <div className="p-6">
          <div className="text-center mb-6">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
              <DollarSign className="h-6 w-6 text-yellow-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Aucun paiement saisi
            </h3>
            <p className="text-sm text-gray-500">
              Vous n'avez pas saisi de paiement pour cette commande. Souhaitez-vous ajouter un paiement maintenant ?
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <ModernButton
              variant="secondary"
              onClick={() => handlePaymentReminderResponse(false)}
              className="flex-1"
            >
              Créer sans paiement
            </ModernButton>
            <ModernButton
              variant="primary"
              onClick={() => handlePaymentReminderResponse(true)}
              className="flex-1"
            >
              Ajouter un paiement
            </ModernButton>
          </div>
        </div>
      </ModernModal>

      {/* Invoice ID Selection Modal */}
      {showInvoiceIdModal && pendingInvoiceData && (
        <ModernModal 
          isOpen={showInvoiceIdModal} 
          onClose={() => {
            setShowInvoiceIdModal(false);
            setPendingInvoiceData(null);
            setCustomInvoiceId('');
            setUseCustomId(false);
            setSelectedPaymentMethod('');
          }} 
          title={`Options PDF ${pendingPdfType === 'facture' ? 'Facture' : pendingPdfType === 'ticket' ? 'Ticket' : 'Fiche'}`} 
          size="md"
        >
          <div className="space-y-6">
            <div className="text-center">
              <p className="text-sm text-neutral-600">
                Choisissez le numéro à afficher sur le PDF
              </p>
            </div>

            <div className="space-y-4">
              {/* Original ID Option */}
              <div 
                onClick={() => {
                  setUseCustomId(false);
                  setCustomInvoiceId('');
                }}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  !useCustomId 
                    ? 'border-primary-500 bg-primary-50' 
                    : 'border-neutral-200 hover:border-primary-200'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <input
                    type="radio"
                    checked={!useCustomId}
                    onChange={() => {
                      setUseCustomId(false);
                      setCustomInvoiceId('');
                    }}
                    className="w-4 h-4 text-primary-600 focus:ring-primary-500"
                  />
                  <div className="flex-1">
                    <h4 className="font-semibold text-neutral-900">
                      Conserver le numéro original
                    </h4>
                    <p className="text-sm text-neutral-600">
                      Utiliser le numéro existant: <strong>{pendingInvoiceData.order.id}</strong>
                    </p>
                  </div>
                </div>
              </div>

              {/* Custom ID Option */}
              <div 
                className={`p-4 border-2 rounded-lg transition-all ${
                  useCustomId 
                    ? 'border-primary-500 bg-primary-50' 
                    : 'border-neutral-200'
                }`}
              >
                <div className="flex items-start space-x-3">
                  <input
                    type="radio"
                    checked={useCustomId}
                    onChange={() => setUseCustomId(true)}
                    className="w-4 h-4 text-primary-600 focus:ring-primary-500 mt-0.5"
                  />
                  <div className="flex-1">
                    <h4 className="font-semibold text-neutral-900 mb-2">
                      Utiliser un nouveau numéro
                    </h4>
                    <p className="text-sm text-neutral-600 mb-3">
                      Saisissez un numéro personnalisé
                    </p>
                    <ModernInput
                      placeholder={`Ex: ${pendingPdfType === 'facture' ? 'FAC' : pendingPdfType === 'ticket' ? 'TICK' : 'FIC'}-2024-001`}
                      value={customInvoiceId}
                      onChange={(e) => setCustomInvoiceId(e.target.value)}
                      disabled={!useCustomId}
                      className={!useCustomId ? 'opacity-50 cursor-not-allowed' : ''}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Method Selection - Only for facture type */}
            {pendingPdfType === 'facture' && (
              <div className="border-t pt-4">
                <h4 className="font-semibold text-neutral-900 mb-3">Mode de paiement</h4>
                <ModernSelect
                  options={[
                    { value: 'Espèce', label: 'Espèce' },
                    { value: 'Carte bancaire', label: 'Carte bancaire' }
                  ]}
                  value={{ value: selectedPaymentMethod, label: selectedPaymentMethod }}
                  onChange={(option) => setSelectedPaymentMethod(option ? option.value : 'Espèce')}
                  placeholder="Sélectionner le mode de paiement"
                />
                <p className="text-xs text-neutral-500 mt-2">
                  Ce mode de paiement sera affiché sur le PDF comme "PAYE EN [mode]"
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t">
              <ModernButton
                variant="secondary"
                onClick={() => {
                  setShowInvoiceIdModal(false);
                  setPendingInvoiceData(null);
                  setCustomInvoiceId('');
                  setUseCustomId(false);
                  setSelectedPaymentMethod('');
                }}
              >
                Annuler
              </ModernButton>
              <ModernButton
                variant="primary"
                onClick={confirmGeneratePDF}
                icon={<FileDown className="w-4 h-4" />}
                disabled={useCustomId && !customInvoiceId.trim()}
              >
                Générer le PDF
              </ModernButton>
            </div>
          </div>
        </ModernModal>
      )}

      {pdfDropdownOpen != null &&
        pdfMenuPos != null &&
        pdfPortalOrder &&
        createPortal(
          <div
            className="pdf-dropdown-portal fixed z-[9999] w-48 max-h-[min(60vh,280px)] overflow-y-auto bg-white rounded-lg shadow-lg border border-gray-200 py-1"
            style={{ top: pdfMenuPos.top, left: pdfMenuPos.left }}
          >
            <button
              type="button"
              onClick={async () => {
                await generateInvoicePDF(pdfPortalOrder);
                setPdfDropdownOpen(null);
              }}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 flex items-center space-x-2 transition-colors duration-150"
            >
              <Download className="w-4 h-4" />
              <span>Facture</span>
            </button>
            <button
              type="button"
              onClick={async () => {
                await generateTicketPDFForOrder(pdfPortalOrder);
                setPdfDropdownOpen(null);
              }}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 flex items-center space-x-2 transition-colors duration-150"
            >
              <Receipt className="w-4 h-4" />
              <span>Ticket</span>
            </button>
            <button
              type="button"
              onClick={async () => {
                await generateFichePDFForOrder(pdfPortalOrder);
                setPdfDropdownOpen(null);
              }}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700 flex items-center space-x-2 transition-colors duration-150"
            >
              <FileText className="w-4 h-4" />
              <span>Fiche</span>
            </button>
          </div>,
          document.body
        )}
    </div>
  );
};

export default VentesCommandes; 